import type { MembersNow } from "shared/mvb/membersNow";
/**
 * Backend wire types - the TypeScript mirror of the Pydantic schemas
 * in backend/framework/schemas.py + backend/shared/auth/router.py.
 *
 * Hand-maintained on purpose (no codegen) so a backend rename surfaces
 * here as a deliberate edit, not a silent fix. The trip-wire: every
 * schema field below is matched by a Pydantic field of the same name,
 * and the Literal unions below MUST exactly match the Literal[...]
 * declarations in the matching Python files.
 *
 * Drift discipline:
 *   - Position / CAPState / Action: see framework/state_machine.py.
 *   - LetterOut / CAPOut / *Summary: see framework/schemas.py.
 *   - MeOut: see shared/auth/router.py.
 *
 * Use as: `import type { CAPOut, Position } from "shared/api/types";`
 * - types are erased at build time, so the cycle-detection rule
 *   (P0.8 module boundaries) doesn't fire on cross-module type imports.
 */

// ── State-machine primitives ────────────────────────────────────────

export type Position = "preparer" | "reviewer" | "approver";
export const ALL_POSITIONS: readonly Position[] = [
  "preparer",
  "reviewer",
  "approver",
] as const;

export type CAPState = "draft" | "in_review" | "approved" | "sent_back";

export type Action = "submit" | "approve" | "send_back" | "withdraw";

// ── Other Literal-shaped fields ─────────────────────────────────────

export type Severity = "low" | "medium" | "high" | "critical";
export type EntryStatus = "open" | "in_progress" | "complete";
export type LetterSourceKind = "pasted" | "pdf_upload";

// ── /v1/me ──────────────────────────────────────────────────────────

export interface MeOut {
  subject: string;
  username: string;
  email: string | null;
  display_name: string | null;
  positions: Position[];
  is_admin: boolean;
  /** P78: the user's raw Keycloak realm roles, for attestation gates
   * (e.g. cro / service_owner signoff) outside the position vocabulary.
   * Optional for tolerant reading -- the backend always sends it (default
   * []), but inline test mocks of MeOut may omit it. */
  realm_roles?: string[];
  /**
   * Round 10 F5 -- which modules this person can actually reach.
   *
   * The chrome gated on the DEPLOYMENT licence, which is off by
   * default, and could not see the per-user entitlement at all: a
   * service owner holding twin and foundation was shown three
   * Framework links and a queue that opened, while every framework
   * call he made returned 403.
   *
   * Optional for tolerant reading -- the backend always sends it, and
   * an inline test mock that omits it should keep the pre-F5
   * behaviour rather than hiding every module.
   */
  modules?: string[];
  /** Round 20 Y1: the internal audit seat reads everything and signs nothing. */
  reads_only?: boolean;
}

// ── Letters ─────────────────────────────────────────────────────────

export interface LetterIntakeIn {
  regulator_code: string;
  title: string;
  /** ISO-8601 datetime string (UTC). */
  received_at: string;
  source_text: string;
}

export interface ObservationOut {
  id: string;
  sequence: number;
  title: string;
  body: string;
  severity: Severity;
  target_capability: string | null;
}

/** C9: an observation written by hand on the letter page. */
export interface ObservationIn {
  title: string;
  body: string;
  severity: Severity;
  target_capability?: string | null;
}
export interface LetterCAPRef {
  id: string;
  title: string;
  state: "draft" | "in_review" | "approved" | "sent_back";
}

export interface LetterOut {
  id: string;
  regulator_code: string;
  title: string;
  received_at: string;
  source_kind: LetterSourceKind;
  ingested_by_user_id: string;
  created_at: string;
  observations: ObservationOut[];
  caps?: LetterCAPRef[]; // P272.3: the CAPs responding to this letter
  /** Round 19 A45 -- the control an internal letter's plan was raised from. */
  raised_from_control?: string | null;
}

export interface LetterSummary {
  id: string;
  regulator_code: string;
  title: string;
  received_at: string;
  source_kind: LetterSourceKind;
  observation_count: number;
  // P272.3: closure rollup for the register.
  cap_count?: number;
  approved_cap_count?: number;
}

// ── CAP entries ─────────────────────────────────────────────────────

export interface CAPEntryIn {
  observation_id: string;
  description: string;
  owner_role?: string | null;
  due_date?: string | null;
}

export interface CAPEntryPatch {
  description?: string | null;
  owner_role?: string | null;
  due_date?: string | null;
  status?: EntryStatus | null;
  completed_evidence_url?: string | null;
}

export interface CAPEntryOut {
  id: string;
  cap_id: string;
  observation_id: string;
  description: string;
  owner_role: string | null;
  due_date: string | null;
  status: EntryStatus;
  completed_at: string | null;
  completed_evidence_url: string | null;
  created_at: string;
  updated_at: string;
}

// ── CAPs ────────────────────────────────────────────────────────────

export interface CAPCreateIn {
  letter_id: string;
  title: string;
}

export interface CAPPatchIn {
  title?: string | null;
}

export interface CAPSummary {
  id: string;
  letter_id: string;
  letter_title: string;
  letter_regulator_code: string;
  title: string;
  state: CAPState;
  current_position: Position;
  entry_count: number;
  updated_at: string;
}

export interface CAPAvailableAction {
  action: Action;
  to_state: string;
  to_position: string;
  actor_position: string;
}

/** C1: one step in a CAP's life. */
export interface CAPTransition {
  id: string;
  from_state: string | null;
  to_state: string;
  from_position: string | null;
  to_position: string;
  actor_username: string | null;
  actor_display_name: string | null;
  actor_position: string;
  comment: string | null;
  captured_at: string;
}

export interface CAPOut {
  id: string;
  letter_id: string;
  title: string;
  state: CAPState;
  current_position: Position;
  current_assignee_user_id: string | null;
  entries: CAPEntryOut[];
  legal_actions: Action[];
  /** C2: the same actions carrying their destination, so a label can
   *  say what the verb does for the seat that holds it. */
  available_actions?: CAPAvailableAction[];
  created_at: string;
  updated_at: string;
}

/** Body for POST /caps/{id}/{submit|approve|send-back|withdraw}. */
export interface WorkflowTransitionIn {
  comment?: string | null;
}

/** Returned by GET /caps/counts. Always has all three keys. */
export type CapCounts = Record<Position, number>;

// ── Cascade view (P2.6) ─────────────────────────────────────────────
//
// Mirrors backend/twin/schemas.py:
//   CascadeChipsOut / CascadeFindingHeadline / CascadeImpactOut /
//   CascadeProductImpact / CascadeSummaryOut
// See docs/p2.6-cascade-plan.md for the contract.

export type ResilienceState = "red" | "amber" | "green" | "unknown";

export interface CascadeChips {
  resilience: ResilienceState | null;
  blast_ibs_count: number | null;
  process_count: number | null;
  obligation_count: number | null;
  /** Round 13 G2: `resilience` is the sealed state floored by the open
   *  findings the card is already showing -- a card cannot list a
   *  critical and print "RESILIENCE green" three lines later.
   *  `resilience_sealed` is what the chain says, and
   *  `resilience_source` says which of the two you are reading. */
  resilience_sealed?: ResilienceState | null;
  resilience_source?: "sealed" | "open_findings";
}

/**
 * P102: one regulatory obligation rollup on an entity's OBLIGATION
 * snapshot. `met` is the assessment overlay (always false today --
 * P101 establishes applicability, not met/open assessment).
 */
export interface ObligationItem {
  id: string;
  code: string;
  title: string;
  citation_ref: string | null;
  jurisdiction: string;
  source_framework: string | null;
  met: boolean;
  derived_from: string;
}

/**
 * P102: the per-entity OBLIGATION chain snapshot
 * (GET /v1/twin/chains/obligation/{kind}/{id}). 404 when no snapshot
 * has been sealed yet (lazy-genesis entity).
 */
/** P272.4: one R/A/G resilience-state snapshot for an entity (the same
 * chain the cascade reads). Used to give the entity detail page its LIVE
 * resilience chip instead of a hardcoded null placeholder. */
export interface ResilienceSnapshot {
  entity_kind: string;
  entity_id: string;
  state: ResilienceState;
  reasons: Record<string, unknown>[];
  captured_at: string;
}

export interface ObligationSnapshot {
  entity_kind: string;
  entity_id: string;
  obligations: ObligationItem[];
  summary: {
    count?: number;
    by_jurisdiction?: Record<string, number>;
  };
  obligation_count: number;
  captured_at: string;
  captured_by: string;
}

/** P105: one control's latest assessment, on the obligation traceability. */
export interface ControlAssessment {
  status: "compliant" | "partial" | "non_compliant" | "no_evidence";
  current_ml: number | null;
  target_ml: number;
}

/** P105: one framework control implementing an obligation. */
export interface TraceabilityControl {
  id: string;
  framework_code: string;
  control_natural_id: string;
  control_text: string;
  domain_name: string;
  subdomain_name: string;
  assessment: ControlAssessment | null;
}

/** P105: the assessment-status tally across an obligation's controls.
 * P108.1 adds `open_cap_count` -- remediating CAPs not yet approved. */
export interface TraceabilitySummary {
  control_count: number;
  assessed: number;
  compliant: number;
  partial: number;
  non_compliant: number;
  no_evidence: number;
  unassessed: number;
  open_cap_count: number;
}

/** P108.1: a CAP remediating one or more controls behind an obligation. */
export interface TraceabilityCap {
  id: string;
  title: string;
  state: string;
  letter_id: string;
  letter_ref: string;
}

/**
 * P105: an obligation traced to the framework controls that implement it
 * (GET /v1/framework/obligations/{id}/traceability) -- the dedup payoff +
 * live compliance behind a surfaced obligation.
 * P108.1 adds `remediating_caps` -- the CAP leg.
 */
export interface ObligationTraceability {
  obligation: {
    id: string;
    natural_id: string;
    title: string;
    jurisdiction: string;
    source_framework: string | null;
    /** P141: Engine-enriched requirement statement (NULL pre-enrichment). */
    engine_summary?: string | null;
  };
  controls: TraceabilityControl[];
  remediating_caps: TraceabilityCap[];
  summary: TraceabilitySummary;
}

/** P108.2: body for POST /caps/{id}/controls. */
export interface CapControlLinkIn {
  framework_control_id: string;
  notes?: string | null;
}

/** P108.1: one control a CAP remediates (CAP-detail "Remediated
 * controls" section). Mirrors the obligation traceability control row. */
export interface CapControl {
  link_id: string;
  framework_control_id: string;
  framework_code: string;
  control_natural_id: string;
  control_text: string;
  domain_name: string;
  subdomain_name: string;
  assessment_status: string | null;
  notes: string | null;
}

/** P115.7: one control a single CAP action item (entry) remediates --
 * the entry-grained companion to CapControl (P115.6 junction). */
export interface CapEntryControl {
  link_id: string;
  cap_entry_id: string;
  framework_control_id: string;
  framework_code: string;
  control_natural_id: string;
  control_text: string;
  notes: string | null;
}

/** P76: attach-body for linking a CAP to a telemetry finding. */
export interface CapFindingLinkIn {
  finding_id: string;
  notes?: string | null;
}

/** P76: one telemetry finding a CAP remediates (CAP-detail "Linked
 * findings" section). */
export interface CapLinkedFinding {
  link_id: string;
  finding_id: string;
  title: string;
  severity: string;
  status: string;
  entity_kind: string;
  /** Round 17 L9 -- the class of an ibs_service subject. */
  entity_service_class?: string | null;
  raised_at: string;
  notes: string | null;
  linked_at: string;
}

/** P76: one CAP that remediates a finding (FindingDetail "Linked CAPs"
 * back-link section). */
export interface FindingLinkedCap {
  link_id: string;
  cap_id: string;
  cap_title: string;
  state: string;
  current_position: string;
  letter_id: string;
  regulator_code: string;
  letter_title: string;
  opened_at: string;
  notes: string | null;
}

/** P106: one step on the bank-level Getting Started ladder. */
export interface OnboardingStep {
  key: string;
  done: boolean;
  count: number;
}

/** P106: the onboarding status (GET /v1/onboarding/status). */
export interface OnboardingStatus {
  lifecycle_stage: "fresh" | "active" | "seasoned";
  show_getting_started: boolean;
  completed: number;
  total: number;
  steps: OnboardingStep[];
}

/** P274: one step on the lifecycle-journey spine. (Named JourneySpine* to
 *  avoid the twin customer-journey `JourneyStep` further down this file.) */
export type JourneySpineStepStatus = "done" | "partial" | "todo" | "blocked";
export interface JourneySpineStep {
  key: string;
  stage: number; // 0-8
  status: JourneySpineStepStatus;
  count: number;
  total: number | null;
  requires: string | null;
  /* Round 12 A7 -- the deployment setting this rung cannot be closed
     from the screens without. Not a block: env-var credentials still
     work, so the rung is reachable, just not from in here. */
  needs_deployment_setting?: string | null;
  /** Round 16 N1 -- the legs open now on a station whose seat moves
   *  (Route 2: reviewer, then CRO, then CIO), and whether it is the
   *  reader's: she holds an open leg or owns a function on it. */
  awaiting_legs?: string[] | null;
  /** Round 17 N9 -- one line per function in flight. */
  awaiting_by_function?: { name: string; leg: string }[] | null;
  /** Round 17 P1 -- the register station's admitted count. */
  admitted?: number | null;
  /** Round 19 B31 -- connectors switched on, and switched off with no run. */
  switched_on?: number | null;
  switched_off_unrun?: number | null;
  /** Round 20 P23 -- the composed baseline beside its linkage count. */
  baseline_pct?: number | null;
  /** Round 20 W3 -- owners named with no sign-in address (scoring station). */
  owners_unbound?: string[] | null;
  owners_unbound_count?: number | null;
  /** Round 20 V1 -- challenged and signed by one person; not counted. */
  not_independent?: number | null;
  /** Round 18 B20 -- the resilience station: entities whose latest seal
   *  is a judged state (not `unknown`), beside `count` derived. */
  judged?: number | null;
  yours?: boolean | null;
}

/** P274: the lifecycle journey status (GET /v1/journey/status). */
export interface JourneySpineStatus {
  lifecycle_stage: "fresh" | "active" | "seasoned";
  completed: number;
  total: number;
  steps: JourneySpineStep[];
}

// ── P276: the document locker ────────────────────────────────────────
export interface DocumentRow extends DocumentReadingCounts {
  id: string;
  sha256: string;
  filename: string;
  source: string;
  source_path: string | null;
  content_type: string | null;
  size_bytes: number;
  title: string | null;
  language: string | null;
  extraction_status: "pending" | "extracted" | "needs_manual_text" | "failed";
  page_count: number | null;
  char_count: number | null;
  created_at: string;
}

export interface DocumentList {
  documents: DocumentRow[];
  total: number;
}

export interface IngestBatchEntry {
  filename: string;
  source_path: string | null;
  sha256: string;
  size_bytes: number;
  content_type: string | null;
  duplicate: boolean;
  duplicate_of: string | null;
}

export interface IngestBatch {
  id: string;
  status: "staged" | "committed" | "discarded";
  entries: IngestBatchEntry[];
  total: number;
  new_count: number;
  duplicate_count: number;
  created_at: string;
}

export interface DocumentCommitResult {
  batch_id: string;
  created: number;
  duplicates_skipped: number;
  extract_run_id: string | null;
}

export interface DocumentTextRow {
  document_id: string;
  extractor: string | null;
  text: string | null;
  /** Round 15 A17 -- passages the original did not show. Null = not examined. */
  hidden_passages?: string[] | null;
  /** And the passages addressed to an automated reader, quoted. */
  directed_passages?: string[];
  /** Round 16 A26 -- which of those the original did not show. */
  hidden_directed_passages?: string[];
}

/** Round 15 A17 -- carried by every row that names a document. */
export interface DocumentReadingCounts {
  directed_passages?: number | null;
  hidden_passages?: number | null;
}

/** P279: control testing -- ToD/ToI/ToE per control. */
export type ControlTestType = "design" | "implementation" | "effectiveness";
export type ControlTestResult =
  "effective" | "partially_effective" | "ineffective" | "not_tested";

export interface ControlTest {
  id: string;
  cycle_id: string;
  framework_control_id: string;
  test_type: ControlTestType;
  result: ControlTestResult;
  method: string | null;
  summary: string | null;
  gaps: string | null;
  tester_user_id: string | null;
  tested_at: string | null;
  derived_from_run_id: string | null;
  /** Round 13 A3: the excerpt the judgement rested on. Null for a test a
   *  person entered by hand, which has no Engine run behind it. */
  evidence_excerpt?: string | null;
  /** The documents that excerpt came from. */
  covering_documents?: string[];
  /** Round 13 A1: non-zero when a mapped document carried an evidence
   *  boundary marker -- i.e. tried to address the Engine. */
  boundary_markers_stripped?: number;
  /** Round 14 A2: passages in the covering documents addressed to an
   *  automated reader, found without asking the model. */
  directive_passages?: DirectivePassage[];
  /** Round 14 A3: documents this result read that no longer cover the
   *  control. The result stands; the record says so. */
  withdrawn_documents?: string[];
  /** Round 14 A9: the maturity the Engine suggested beside its result --
   *  carried, never applied to the assessment. */
  suggested_ml?: number | null;
  /** Round 14 A1: who accepted the Engine result this row now carries.
   *  Null on a result a person entered by hand. */
  accepted_by_user_id?: string | null;
  accepted_at?: string | null;
  /** Round 14 A6: the people, by name. */
  accepted_by_name?: string | null;
  tester_name?: string | null;
  /** Round 14 A1: the Engine's pending judgement. It is NOT the result:
   *  the rollup never reads it, and only an accept makes it one. */
  proposal?: ControlTestProposal | null;
  /** Round 18 A35: the proposals a person declined, oldest first. */
  declines?: DeclinedProposal[];
}

export interface DeclinedProposal {
  declined_at: string;
  declined_by_name: string | null;
  declined_result: ControlTestResult | null;
}

export interface DirectivePassage {
  /** The document's title, or its filename. */
  document: string;
  passage: string;
  /** Round 16 A26 -- the original did not show this passage. */
  hidden?: boolean;
}

export interface ControlTestProposal {
  result: ControlTestResult;
  summary: string | null;
  gaps: string | null;
  run_id: string | null;
  proposed_at: string | null;
  evidence_excerpt?: string | null;
  covering_documents?: string[];
  boundary_markers_stripped?: number;
  directive_passages?: DirectivePassage[];
  withdrawn_documents?: string[];
  suggested_ml?: number | null;
  /** Round 16 A24 -- a positive result whose own reasoning says the
   *  requirement is not evidenced. */
  reasoning_disagrees?: boolean;
}

export interface ControlTestList {
  tests: ControlTest[];
  rolled_up_status: string | null;
  /** Round 13 A5: what the assessment actually says. */
  assessment_status?: string | null;
  /** Round 13 A5: the assessment states a position and no test supports
   *  it -- e.g. after a design test was set back to "Not tested". */
  status_unsupported?: boolean;
  /** ROUND 15 A18 -- a hand grade with no test ever recorded. */
  no_test_recorded?: boolean;
}

export interface ControlTestUpsertIn {
  result: ControlTestResult;
  method?: string | null;
  summary?: string | null;
  gaps?: string | null;
}

export interface ControlTestCoveringDocument extends DocumentReadingCounts {
  document_id: string;
  filename: string;
  title: string | null;
}

export interface TestDesignResult {
  test: ControlTest;
  /** Round 13 A2: what the rollup WOULD set, not what it did. */
  rolled_up_status: string | null;
  covering_documents: ControlTestCoveringDocument[];
  /** What the assessment says now. Null when there is no assessment yet. */
  current_status: string | null;
  /** Accepting would materialise the bank's first position on this control. */
  would_create_assessment: boolean;
  /** True while the proposal stands unapplied. */
  awaiting_confirmation: boolean;
  /** Why accepting would do nothing, in words. */
  blocked_reason: string | null;
}

/** P280: control KPIs (effectiveness metrics) + ToE derivation. */
export interface KpiReadingRow {
  id: string;
  value: number;
  in_tolerance: boolean;
  source: string;
  observed_at: string;
  /**
   * C7: what the number counted. Null where the metric has no
   * comparable list -- an average availability is not "these
   * services" -- so the reader can tell "nothing contributed" from
   * "this measure has no contributors to show".
   */
  contributing_ids?: string[] | null;
  /** C3: what each contributor is, keyed by id. */
  contributor_labels?: Record<string, string> | null;
  /** What those ids are, so a consumer knows where to link them. */
  contributor_kind?: string | null;
}

export interface ControlKpi {
  id: string;
  framework_control_id: string;
  name: string;
  source: "auto" | "manual";
  metric_key: string | null;
  target: number;
  tolerance: number;
  direction: "higher_is_better" | "lower_is_better";
  unit: string | null;
  frequency: string | null;
  latest_reading: KpiReadingRow | null;
  /** C3: when this metric next moves, and whether anything is taking
   *  it. Null with sampling on and nothing sampled yet. */
  next_sample_at?: string | null;
  sampling_enabled?: boolean;
}

/** What one estate-wide auto-KPI sampling pass did (round 11 D5). */
export interface KpiSampleResult {
  kpis: number;
  readings: number;
  skipped: number;
}

export interface AutoMetricMeta {
  key: string;
  label: string;
  direction: string;
  unit: string;
}

export interface ControlKpiList {
  kpis: ControlKpi[];
  /** Round 11 D5: whether anything is taking auto readings at all, so
   *  a panel with no KPIs yet can still say what an auto metric will
   *  do -- which is when the reader can still choose differently. */
  sampling_enabled?: boolean;
  auto_metrics: AutoMetricMeta[];
}

export interface ControlKpiCreateIn {
  name: string;
  source: "auto" | "manual";
  metric_key?: string | null;
  target: number;
  tolerance?: number;
  direction?: string | null;
  unit?: string | null;
}

export interface KpiOutcome {
  kpi_id: string;
  name: string;
  latest_value: number | null;
  in_tolerance: boolean | null;
}

export interface TestEffectivenessResult {
  test: ControlTest;
  rolled_up_status: string | null;
  kpi_outcomes: KpiOutcome[];
}

/** P282: implementation probes (categorical ToI signals). */
export type ProbeStatus = "present" | "partial" | "absent" | "unknown";

export interface ControlProbe {
  id: string;
  framework_control_id: string;
  name: string;
  probe_key: string;
  last_status: ProbeStatus;
  last_run_at: string | null;
}

export interface ProbeCatalogueEntry {
  key: string;
  label: string;
  checks: string;
}

export interface ControlProbeList {
  probes: ControlProbe[];
  catalogue: ProbeCatalogueEntry[];
}

export interface ControlProbeCreateIn {
  probe_key: string;
  name?: string | null;
}

export interface TestImplementationResult {
  test: { id: string; test_type: string; result: ControlTestResult };
  rolled_up_status: string | null;
  probe_statuses: ControlProbe[];
}

/** P278.3: document gap analysis -- expected deliverables vs documents held. */
export type GapStatus = "present" | "candidate" | "missing";

export interface GapCoveringDocument extends DocumentReadingCounts {
  document_id: string;
  filename: string;
  title: string | null;
  link_status: string;
  confidence: number;
  /** Round 13 A4: what `reject` is keyed on. Without it the one verb that
   *  undoes "This covers it" could not be called from the screen that
   *  offers it. */
  link_id: string;
}

export interface DeliverableGap {
  deliverable_id: string;
  framework_code: string;
  name: string;
  deliverable_type: string;
  mandatory: boolean;
  status: GapStatus;
  covering_documents: GapCoveringDocument[];
}

export interface FrameworkGapSummary {
  framework_code: string;
  total: number;
  present: number;
  candidate: number;
  missing: number;
  mandatory_missing: number;
  coverage_pct: number;
}

export interface DocumentGapReport {
  summaries: FrameworkGapSummary[];
  deliverables: DeliverableGap[];
}

/** P286.3: reverse view -- held documents ranked for an uncovered deliverable. */
export interface CandidateDocument {
  document_id: string;
  filename: string;
  title: string | null;
  similarity: number;
  /** Round 18 A35 -- once confirmed for this deliverable and withdrawn. */
  withdrawn?: boolean;
}

export interface CandidateDocuments {
  deliverable_id: string;
  deliverable_name: string;
  documents: CandidateDocument[];
}

/** P275: the IBS entity-lifecycle hub (GET /v1/twin/ibses/{id}/story). */
export interface DependencyLayerItem {
  kind: string;
  id: string | null;
  name: string;
  /** Free text: composed prose, or the bank's own data. Verbatim. */
  detail: string | null;
  /** F3: the qualifiers that are OUR closed vocabularies, so the
   *  reader's catalogue turns them into words. Printed raw, they ended
   *  every row of the six-layer block in a column value. */
  detail_terms?: string[];
}

/** One of the six layers the regime asks an IBS to be mapped across. */
export interface DependencyLayer {
  layer: string;
  /** direct | transitive | absent | unreachable | not_applicable. Only
   *  `absent` is a gap: `unreachable` means an upstream layer is empty
   *  and `not_applicable` is something the bank has declared. */
  reach: string;
  path: string;
  /** Round 10 D2: the same path as catalogue keys, rendered in the
   *  reader's language with a mirroring separator. */
  path_hops?: string[];
  items: DependencyLayerItem[];
  item_total: number;
  truncated: boolean;
  /** False for data, which is a free-text column rather than an entity. */
  modelled: boolean;
  note: string | null;
  /** Round 16 I14: the note as a catalogue key, where it has one. */
  note_key?: string | null;
  resolved: boolean;
  is_gap: boolean;
}

export interface DependencyStack {
  ibs_id: string;
  ibs_code: string;
  ibs_name: string;
  customer_facing: boolean;
  layers: DependencyLayer[];
  resolved_count: number;
  /** A2: layers the bank DECLARED it does not have -- an answer, not
   *  a shortfall. */
  by_design_count?: number;
  accounted_count?: number;
  gap_layers: string[];
}

/** Stitching review 5.2 -- one matter's thread, GET /v1/thread/ibs/{id}. */
export type ThreadStageKey = "expected" | "have" | "judged" | "decided" | "proved";
export type ThreadStageStatus = "done" | "attention" | "missing";

export interface ThreadStage {
  key: ThreadStageKey;
  status: ThreadStageStatus;
  /** Named figures; ints, short codes and names only. */
  facts: Record<string, number | string | null>;
  /** Round 17 R2 -- `count` is how many hold the seat when none is named.
   *  Round 18 R7 -- `verb` "approved" is a decision taken, by `names`, on `on`. */
  holder: {
    role: string;
    names: string[];
    count?: number;
    /** Round 19 R19 -- "sealed": a sealed record, held by those who signed it. */
    verb?: "held" | "approved" | "sealed";
    on?: string | null;
    /** Round 18 R9 -- each name's seat, for a two-seat decision. */
    roles?: string[];
  };
  next: { action: string; href: string } | null;
  /** Round 17 R5 -- records the stage names, each opening itself. */
  links?: { label: string; href: string }[];
}

export interface IBSThread {
  subject_kind: string;
  subject_id: string;
  subject_name: string;
  is_function: boolean;
  stages: ThreadStage[];
}

export interface IBSStory {
  id: string;
  code: string;
  name: string;
  /** Round 16 -- the register class; a CIF is not "this IBS". */
  service_class?: "customer" | "state" | "internal";
  /** Round 16 M4 -- what this function funds: what stops when it stops. */
  funds?: { id: string; name: string; admitted: boolean }[];
  /** P421 / round 19 G40 -- the changes on this service; counts of 0
   *  when none has been raised. */
  change?: {
    reached_count: number;
    /** Any change naming it, or a service or item it rests on. */
    touching_count?: number;
    latest_id: string | null;
    latest_title: string | null;
    latest_raised_at: string | null;
    latest_significance?: "significant" | "routine" | null;
  } | null;
  outcome_category: string;
  sovereign_flag: boolean;
  admission: {
    status: string;
    workflow_state: string | null;
    criticality_tier: string | null;
    inclusion_decision: string | null;
    version: number | null;
    signatures?: {
      role: string;
      name: string | null;
      at: string | null;
      note: string | null;
    }[];
  } | null;
  tolerance: {
    hours: number | null;
    signed: boolean;
    /** C1: the state name from the one composer, so this cannot
     *  disagree with the owner's home or the register. */
    status?: string;
    proposed_hours?: number | null;
    awaiting_role?: string | null;
  };
  mvb: {
    /** Projected by an approved ceremony -- and settable by CSV, which
     *  is why it is not on its own proof that one happened (A4). */
    flag: boolean;
    /** Whether an approved declaration is actually in force. */
    declaration_approved?: boolean;
    /** The candidate register's recommendation for the NEXT
     *  declaration, which is a different question. */
    tier: string | null;
    recommendation_differs?: boolean;
  };
  resilience: {
    state: string;
    /* Round 12 B1 -- a sealed green on an estate nobody has looked at
       means "nothing has reported a problem", not "this is healthy". */
    estate_assessed?: boolean;
  } | null;
  obligations_count: number;
  scenarios_count: number;
  /** P428.2: the group this service is governed in, if any. */
  group_code?: string | null;
  group_name?: string | null;
  /** Round 20 V5 -- no recorded owner, and its open critical findings. */
  owner_recorded?: boolean;
  open_critical?: number;
}

export interface CascadeFindingHeadline {
  id: string;
  title: string;
  severity: Severity;
  /** ISO-8601 datetime string. */
  raised_at: string;
  external_url: string | null;
}

/**
 * P16.2 (#485): one customer journey breadcrumb on an impacted IBS.
 * Empty `step_names` is valid (e.g. a journey with no steps yet);
 * the impact card renders "(no steps defined)" in that case.
 *
 * P19.1 (#533): `failing_step_number` + `failing_state` carry the
 * state-driven failing-step inference. RED -> step 1 fails;
 * AMBER -> last step degrades; GREEN / UNKNOWN / no per-(IBS,
 * channel) snapshot -> both fields null. IBS-pillar state does
 * NOT fall back -- only the per-channel override drives the
 * inference. The frontend tints the chip at
 * `failing_step_number` accordingly.
 */
export interface CascadeImpactJourney {
  journey_id: string;
  channel_id: string;
  channel_code: string;
  channel_name: string;
  step_count: number;
  step_names: string[];
  failing_step_number: number | null;
  failing_state: "red" | "amber" | null;
  /**
   * P28.1 (#540): distinguishes "entity" (a tagged
   * JourneyStep.service_id / capability_id / vendor_id has an
   * open Finding -- the walker located the failing step
   * precisely) from "state" (P19's coarser per-channel state
   * heuristic). `null` when there is no failing step. UI surface
   * is backlog `#565`; v0.28.0 ships the data only.
   */
  failing_step_attribution_source: "entity" | "state" | null;
}

export interface CascadeImpact {
  entity_kind: string;
  entity_id: string;
  entity_name: string | null;
  open_finding_count: number;
  headline_finding: CascadeFindingHeadline;
  /** Round 19 R18 -- every other open tolerance breach on the entry. */
  breach_findings?: CascadeFindingHeadline[];
  /** JSONB passthrough from BlastRadiusSnapshot.blast_nodes. */
  cascade_nodes: Record<string, unknown>[];
  cascade_edges: Record<string, unknown>[];
  cascade_total_nodes: number;
  cascade_ibs_count: number;
  /** Round 17 P2 -- the war room's other cuts: candidate services and
   *  critical internal functions the blast reaches. Removed rows are in
   *  neither. Optional for payloads that predate them. */
  cascade_candidate_count?: number;
  cascade_function_count?: number;
  /** Round 19 B28 -- of those functions, the admitted ones. */
  cascade_function_admitted_count?: number;
  /** Round 20 P20 -- services reached on the way, the entity aside. */
  cascade_service_count?: number;
  /** Round 18 B17 -- an IBS card's admission status; null otherwise. */
  admission_status?: string | null;
  /** D2 -- the SAMA outcome an impacted IBS belongs to; null for every
   *  other kind. Lets the board's per-outcome tile open the page
   *  showing the set it counted. */
  outcome_category?: string | null;
  /** B4 -- what an IBS rests on. Null where the direction does not
   *  apply: a service card's own downstream reach is the answer. */
  upstream_count?: number | null;
  /** Round 16 M1 -- what this function funds: they stop when it stops. */
  funds?: { id: string; name: string }[];
  /** Round 16 L2 -- the register class of an ibs_service impact. */
  service_class?: "customer" | "state" | "internal" | null;
  chips: CascadeChips;
  /**
   * P16.2 (#485): journey breadcrumbs for impacted IBSes. Empty
   * list for non-IBS impacts (vendor/service/capability) and for
   * IBSes with no journeys defined.
   */
  customer_journeys: CascadeImpactJourney[];
}

export interface CascadeProductImpact {
  product_id: string;
  product_code: string;
  product_name: string;
  classification: string;
  impacted_via_ibs_ids: string[];
  /**
   * P14.1 (#478): SAR annual revenue this product carries. Pydantic
   * v2 serialises Decimal as a string ("1850000.00"), so the type
   * is `string | null`. `null` == "not measured" -- DON'T treat as
   * zero (rollup excludes NULL products explicitly).
   */
  annual_revenue_at_risk: string | null;
  /**
   * P14.1 (#478): annual / 365 quantised to 2dp by the server. Same
   * `string | null` convention as `annual_revenue_at_risk`.
   */
  daily_revenue_at_risk: string | null;
  /**
   * P23.1 (#524): how the at-risk figure was computed.
   *   "uniform" -> all of the product's links had NULL
   *                revenue_share; walker apportioned as
   *                impacted_count / total_count * annual_revenue
   *   "split"   -> at least one link carried an explicit
   *                revenue_share; walker summed the explicit
   *                impacted shares (NULL impacted = 0)
   *   null      -> product has no annual_revenue (unmeasured)
   */
  at_risk_mode: "uniform" | "split" | null;
}

export interface CascadeSummary {
  /* Round 12 B1 -- is there anything here to be all-clear ABOUT?
     "All clear. No open critical or high findings impact any service
     right now" was rendered over an estate with nothing in it: zero
     impacted because nothing is wrong and zero impacted because there
     is nothing there are the same zero. Optional so an older stub
     reads as assessed, which is what it was. */
  estate_assessed?: boolean;
  /** ISO-8601 datetime string. Echoes the resolved point-in-time. */
  as_of: string;
  severities: string[];
  impacted_count: number;
  /**
   * Impacted IBSes (directly impacted + reached via cascade) -- the
   * truthful "IBSes impacted" headline; matches the board. Distinct from
   * cascade_ibs_count, which is the downstream blast-walk union only.
   */
  ibs_impacted_count: number;
  impacted_by_severity: Record<string, number>;
  cascade_node_count: number;
  cascade_ibs_count: number;
  /** Round 19 P18 -- across the cards, the candidates and functions the
   *  reach adds: the union the war room judges. */
  cascade_candidate_union_count?: number;
  cascade_function_union_count?: number;
  product_count: number;
  /**
   * P14.1 (#478): SAR rollup of measured products only. NULL revenue
   * rows are excluded from the sum + counted in `revenue_unmeasured_count`
   * so the UI can show a "M of N measured" ratio. Decimal as string,
   * same convention as the per-impact fields.
   */
  total_annual_revenue_at_risk: string;
  total_daily_revenue_at_risk: string;
  revenue_measured_count: number;
  revenue_unmeasured_count: number;
  /**
   * P20.1 (#519): MVB intersection rollup. `mvb_impacted_count`
   * is the count of direct IBS impacts with `mvb_flag = true`;
   * `mvb_total_count` is the bank-wide MVB IBS count;
   * `mvb_impacted_share` is `impacted/total` rounded to 4dp,
   * `null` when no MVB has been declared. Same shape on
   * `/v1/cascade` AND `/v1/scenarios/{id}/cascade`.
   */
  mvb_impacted_count: number;
  mvb_total_count: number;
  mvb_impacted_share: number | null;
  /** P429.6 -- Critical Internal Functions among the impacted rows. */
  cif_impacted_count?: number;
  /**
   * P258: estate-wide IBS resilience RAG from the sealed resilience-state
   * chain (dependency-propagated). Counts the IBSes currently red / amber,
   * distinct from the finding-walk counts above -- a CI finding can leave
   * cascade_ibs_count at 0 yet still drive its dependent IBSes amber/red.
   */
  ibs_resilience_red: number;
  ibs_resilience_amber: number;
  impacted_products: CascadeProductImpact[];
  impacted: CascadeImpact[];
}

// ── Capability map (P2.7) ───────────────────────────────────────────
//
// Mirrors backend/twin/schemas.py:
//   CapabilityMapCell / CapabilityMapColumn / CapabilityMapOut
// See docs/p2.7-capability-map-plan.md for the contract.

export interface CapabilityMapCell {
  capability_id: string;
  code: string;
  name: string;
  /** 1..5 -- the tier the capability sits at in the grid's column axis. */
  tier: number;
  parent_capability_id: string | null;
  /** Reuses the 4-chip rollup shape from cascade. */
  chips: CascadeChips;
  /** Open critical+high finding count -- the at-a-glance badge. */
  open_finding_count: number;
  /** Empty in v0.2.0 (backlog #430); shipped in the shape so the
   *  frontend can iterate the CellDrawer once the link table lands. */
  impacted_service_ids: string[];
}

export interface CapabilityMapColumn {
  /** 1..5. Always 5 columns in a response, even when empty. */
  tier: number;
  capabilities: CapabilityMapCell[];
}

export interface CapabilityMap {
  /** ISO-8601 datetime string. */
  as_of: string;
  /** Always 5 columns (tier 1..5) so the grid layout stays stable. */
  columns: CapabilityMapColumn[];
}

// ── Entity detail basic shape (P2.7) ────────────────────────────────

export type EntityKind =
  "ibs_service" | "service" | "capability" | "vendor" | "configuration_item";

export interface EntityBasic {
  id: string;
  kind: EntityKind;
  code: string;
  name: string;
  description: string | null;
  /** Round 16 -- the register class, on an ibs_service only. */
  service_class?: "customer" | "state" | "internal" | null;
}

// ── Findings filter (P2.7) ──────────────────────────────────────────

// ── Chain TREND (P2.9) ──────────────────────────────────────────────

export interface ResilienceTrendCounts {
  red: number;
  amber: number;
  green: number;
  unknown: number;
}

export interface ResilienceTrendPoint {
  /** ISO date "YYYY-MM-DD". */
  day: string;
  counts: ResilienceTrendCounts;
}

export interface ResilienceTrend {
  chain: "resilience-state";
  window_days: number;
  entity_kind: string | null;
  /** Dense chronological series, oldest -> newest. */
  points: ResilienceTrendPoint[];
}

export interface CountTrendPoint {
  day: string;
  total: number;
}

export interface CountTrend {
  chain: "blast-radius" | "process" | "obligation";
  window_days: number;
  entity_kind: string | null;
  /** "total_nodes" / "process_count" / "obligation_count". */
  count_field: string;
  points: CountTrendPoint[];
}

// ── Chain DIFF (P2.8) ───────────────────────────────────────────────

export type ChainName = "resilience-state" | "blast-radius" | "process" | "obligation";

export interface ScalarChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface CountDelta {
  field: string;
  before: number;
  after: number;
}

export interface ListChange {
  field: string;
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
}

export interface ChainDiff {
  chain: string;
  entity_kind: string;
  entity_id: string;
  /** ISO datetime; serialised as `from` per Pydantic alias. */
  from: string;
  to: string;
  from_snapshot: Record<string, unknown>;
  to_snapshot: Record<string, unknown>;
  derivation_version_changed: boolean;
  derivation_version_before: number | null;
  derivation_version_after: number | null;
  scalar_changes: ScalarChange[];
  count_deltas: CountDelta[];
  list_changes: ListChange[];
  is_zero_delta: boolean;
}

export interface Finding {
  id: string;
  source_system_id: string;
  external_ref: string;
  external_url: string | null;
  title: string;
  description: string | null;
  severity: Severity;
  status: string;
  /** P347 curated taxonomy; null on connector rows without a clean map. */
  category?: string | null;
  entity_kind: string;
  entity_id: string;
  /** ISO-8601 datetime string. */
  raised_at: string;
  /** ISO-8601 datetime string. */
  closed_at: string | null;
  payload: Record<string, unknown>;
  /** Source-system provenance -- populated by the per-finding detail
   * endpoint (P199); null on the lean list surfaces. `telemetry.auto`
   * marks a finding auto-raised from a down telemetry service. */
  source_system_code?: string | null;
  source_system_label?: string | null;
  /** Round 16 L4 -- the subject's register class, on an ibs_service. */
  entity_service_class?: "customer" | "state" | "internal" | null;
  /** Round 14 G -- the person who raised a finding by hand. */
  raised_by_name?: string | null;
  /** D7 -- who the estate records as accountable for the thing this
   *  finding is about. The page named nobody. */
  entity_owner_name?: string | null;
  entity_owner_email?: string | null;
  /** Round 9 C4: the desk working the incident, which is a different question from who is accountable for it. */
  responding_team?: string | null;
  /** Round 10 C1: every team on it, via the CI / business-service reroute. */
  responding_teams?: string[];
  /** Round 10 C1: how many people an added comment would tell. */
  comment_audience?: number;
  /** Round 20 U2: the same audience by name. */
  comment_audience_names?: string[];
  /** Round 20 U2: who has been told of this finding, oldest first. */
  told?: FindingTold[];
  /** Round 20 U2: notices held for people who have not signed in yet. */
  told_held?: number;
}

/** Round 20 U2 -- one person told of a finding. */
export interface FindingTold {
  name: string;
  told_at: string;
  kind: string;
  emailed: boolean;
  acknowledged_at: string | null;
}

// -- Landscape view (P6) -------------------------------------------

export type LandscapeConfidence = "high" | "medium" | "low" | "unverified";
export type LandscapeOverlay = "confidence" | "mvb" | "cascade" | "both";
export type LandscapePillar = "red" | "amber" | "green" | "unknown";

export interface MappedServiceSummary {
  id: string;
  name: string;
  confidence: LandscapeConfidence;
}

export interface LandscapeTileRollup {
  id: string;
  code: string;
  name: string;
  pascal_name: string | null;
  sort_order: number;
  mapped_service_count: number;
  mvb_linked_count: number;
  worst_resilience_pillar: LandscapePillar | null;
  worst_finding_severity: Severity | null;
  best_confidence: LandscapeConfidence | null;
  // P9.1 additions for the classic view. v0.6.0+ overlay view
  // ignores these fields transparently.
  confidence_breakdown: Record<LandscapeConfidence, number>;
  mapped_services: MappedServiceSummary[];
  mapped_services_truncated: boolean;
  // P29.1 (#504): BIAN-domain context back-filled from v1's
  // tiles.csv. All four are null for non-BIAN tiles + the
  // handful of BIAN tiles without populated `catalog_attrs`;
  // the classic-view popover renders each section conditionally.
  description: string | null;
  control_record: string | null;
  behaviour_qualifiers: string[] | null;
  operations: string[] | null;
}

export interface LandscapeSectionView {
  id: string;
  code: string;
  name: string;
  sort_order: number;
  tiles: LandscapeTileRollup[];
}

export interface LandscapeZoneView {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color_hex: string | null;
  sort_order: number;
  sections: LandscapeSectionView[];
}

export interface LandscapeView {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sort_order: number;
  /** ISO-8601 datetime. Resolved by the backend (NOW() when as_of omitted). */
  as_of: string;
  confidence_min: LandscapeConfidence;
  overlay: LandscapeOverlay;
  zones: LandscapeZoneView[];
}

export interface TileMappedService {
  service_id: string;
  service_code: string;
  service_name: string;
  classification: string;
  confidence: LandscapeConfidence;
  derived_method: "import" | "ai" | "manual";
  /** ISO-8601 datetime. */
  derived_at: string;
  notes: string | null;
  resilience_pillar: LandscapePillar | null;
  open_finding_count: number;
  is_mvb_linked: boolean;
}

export interface TileDetail {
  id: string;
  code: string;
  name: string;
  pascal_name: string | null;
  section_id: string;
  /** ISO-8601 datetime. */
  as_of: string;
  mapped_services: TileMappedService[];
}

// -- MVB module (P7) -----------------------------------------------

export type MvbStatus = "draft" | "open" | "approved" | "superseded";
export type MvbReadiness = "cold" | "warm" | "hot" | "live";
export type MvbTechStack = "basic_isolated" | "mirror_production";
export type MvbSync = "longer_passive" | "shorter_passive";
export type MvbExit = "transfer_back" | "become_new_prod" | "integrate";
export type MvbSignOffRole = "cro" | "ae";

/** What a signer agreed to, reduced to something comparable. */
export interface MvbSignoffScope {
  digest: string;
  ibs_count: number;
  service_count: number;
  vendor_count: number;
  ci_count?: number;
}

export interface MvbDeclaration {
  /** Round 20 B32 -- per leg, the seat its signer held at signing. */
  signer_seats?: Record<
    string,
    { seat_at_signing: string | null; holds_seat: boolean }
  >;
  signoff_complete?: boolean;
  effective_before_signature?: boolean;
  /** Round 20 G43 -- a review date before the declaration comes into force. */
  review_before_effective?: boolean;
  /** Round 18 L10 -- each frozen member's class and admission today. */
  members_now?: MembersNow | null;
  id: string;
  name: string;
  description: string | null;
  rationale: string | null;
  effective_from: string | null;
  effective_to: string | null;
  status: MvbStatus;

  submitter_user_id: string | null;
  submitted_at: string | null;

  submitted_note?: string | null;
  submitter_name?: string | null;

  cro_user_id: string | null;
  cro_signed_at: string | null;
  cro_signoff_name?: string | null;
  cro_signoff_note?: string | null;

  ae_user_id: string | null;
  ae_signed_at: string | null;
  ae_signoff_name?: string | null;
  /** Round 13 E6 -- the set each signature was given over. */
  cro_signoff_scope?: MvbSignoffScope | null;
  ae_signoff_scope?: MvbSignoffScope | null;
  ae_signoff_note?: string | null;

  snapshot: Record<string, unknown>;

  superseded_by_id: string | null;

  readiness_state: MvbReadiness | null;
  tech_stack_mode: MvbTechStack | null;
  sync_model: MvbSync | null;
  recovery_exit_strategy: MvbExit | null;

  mvb_threshold_hours: number;
  review_due_at: string | null;

  created_at: string;
  updated_at: string;
}

// -- Channel heatmap (P8) ------------------------------------------

export interface HeatmapChannel {
  id: string;
  code: string;
  name: string;
  color_hex: string | null;
  sort_order: number;
}

export interface HeatmapIBS {
  id: string;
  code: string;
  name: string;
  outcome_category: string;
  mvb_flag: boolean;
  /** Round 13 B1: "not_assessed" when nobody has judged this service,
   *  whatever the resilience chain honestly recorded. */
  resilience_pillar: LandscapePillar | "not_assessed" | null;
  /** Round 13 B1: whether anybody has judged it. */
  assessed?: boolean;
}

export interface HeatmapCell {
  ibs_id: string;
  channel_id: string;
  /**
   * P18 (#500): cell's effective resilience state. The per-(IBS,
   * channel) snapshot wins; otherwise inherits IBS-level pillar.
   * `null` only when neither has a snapshot yet.
   */
  state: LandscapePillar | null;
  /**
   * P18 (#500): which path produced `state`. "channel" means an
   * explicit per-cell override exists; "ibs" means falling back
   * to the row's IBS-level pillar.
   */
  state_source: "channel" | "ibs";
}

export interface ChannelHeatmap {
  as_of: string;
  channels: HeatmapChannel[];
  ibses: HeatmapIBS[];
  cells: HeatmapCell[];
}

export interface MvbScorecard {
  /** Round 18 K27 -- true here; see MvbScorecardNone for the other answer. */
  in_force?: true;
  declaration_id: string;
  declaration_name: string;
  effective_from: string | null;
  effective_to: string | null;
  ibs_count: number;
  /** Round 18 L10 -- the set by class and admission today. */
  members_now?: MembersNow;
  ibs_by_outcome_category: Record<string, number>;
  service_count: number;
  vendor_count: number;
  configuration_item_count: number;
  readiness_state: MvbReadiness | null;
  tech_stack_mode: MvbTechStack | null;
  sync_model: MvbSync | null;
  recovery_exit_strategy: MvbExit | null;
  mvb_threshold_hours: number;
  review_due_at: string | null;
  review_due_in_days: number | null;
  cro_signed_at: string | null;
  ae_signed_at: string | null;
  dual_signoff_complete: boolean;
}

/** Round 18 K27 -- no declaration in force: an answer, not a 404. */
export interface MvbScorecardNone {
  in_force: false;
}

// -- Framework assessments (P10) -----------------------------------

export type CycleStatus = "draft" | "open" | "closed";
export type AssessmentStatus =
  "compliant" | "partial" | "non_compliant" | "no_evidence";
export type WorkflowPattern = "peer" | "signoff";
export type AssessmentState =
  "draft" | "in_review" | "in_signoff" | "approved" | "sent_back";
export type AssessmentAction = "submit" | "approve" | "send_back" | "withdraw";
export type AnalyserStatus = "queued" | "running" | "succeeded" | "failed";

export type AnalyserMode = "single_control" | "batch_cycle";

export interface FrameworkSummary {
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

/**
 * Round 10 F1 -- where a control is graded, so a search hit can land
 * somewhere that opens it.
 *
 * `cycle_id` is null when the framework has no cycle at all. That is a
 * real state, not an error, and the hub says so rather than routing
 * into a cycle that does not exist.
 */
export interface ControlLocation {
  control_id: string;
  control_natural_id: string;
  framework_code: string;
  framework_name: string | null;
  cycle_id: string | null;
  cycle_name: string | null;
  cycle_status: string | null;
  cycle_count: number;
}

export interface FrameworkControl {
  id: string;
  control_natural_id: string;
  control_text: string;
  display_order: number;
  default_target_ml: number;
}

export interface FrameworkSubdomain {
  subdomain_id: string;
  subdomain_name: string;
  principle: string | null;
  controls: FrameworkControl[];
}

export interface FrameworkDomain {
  domain_id: string;
  domain_name: string;
  domain_description: string | null;
  subdomains: FrameworkSubdomain[];
}

export interface FrameworkCatalogue {
  code: string;
  name: string;
  description: string | null;
  total_controls: number;
  domains: FrameworkDomain[];
}

export interface FrameworkCycle {
  id: string;
  framework_code: string;
  name: string;
  description: string | null;
  period_start: string;
  period_end: string;
  due_date: string;
  status: CycleStatus;
  closed_at: string | null;
  /**
   * P22.1 (#522): admins toggle this to auto-fire the
   * single-control Marsad Engine analyser when a preparer
   * attaches evidence to any assessment in this cycle.
   */
  auto_analyse_on_evidence_attach: boolean;
  created_at: string;
  updated_at: string;
  /** Round 20 Y4 -- assessments resting on evidence older than the cycle. */
  assessments_on_old_evidence?: number;
}

/** P222: per-open-cycle assessment coverage for the Home progress card. */
export interface CycleProgress {
  id: string;
  framework_code: string;
  name: string;
  due_date: string;
  controls_total: number;
  controls_graded: number;
  coverage_pct: number;
  /** P236.2: assessments through the workflow (approved). */
  controls_signed_off: number;
  signoff_pct: number;
  is_overdue: boolean;
  days_to_due: number;
}

export interface CycleCreateIn {
  framework_code: string;
  name: string;
  description?: string | null;
  period_start: string;
  period_end: string;
  due_date: string;
}

export interface CyclePatchIn {
  /** P22.1 (#522): partial update. v0.22.0 only exposes the toggle. */
  auto_analyse_on_evidence_attach?: boolean;
}

export interface FrameworkAssessment {
  id: string;
  framework_code: string;
  cycle_id: string;
  framework_control_id: string;
  status: AssessmentStatus;
  current_ml: number | null;
  target_ml: number;
  evidence_summary: string | null;
  gaps: string | null;
  recommendations: string | null;
  notes: string | null;
  assignee_user_id: string | null;
  approver_user_id: string | null;
  /** E6 -- resolved by the API; null when the id names nobody. */
  assignee_username?: string | null;
  /** D2 -- who made the judgement, from the seal. Not the assignee. */
  graded_by_username?: string | null;
  /** D4 -- when the judgement was sealed. The row's updated_at moves
   *  whenever anything on the row does; this is when somebody made the
   *  call. */
  graded_at?: string | null;
  /** Round 17 A29 -- who last moved the grade (approve / send_back). */
  moved_by_username?: string | null;
  moved_action?: string | null;
  approver_username?: string | null;
  workflow_pattern: WorkflowPattern;
  workflow_state: AssessmentState | null;
  due_date: string | null;
  reviewed_at: string | null;
  derived_from_run_id: string | null;
  /**
   * C7: which control tests hold a real result for this control in
   * this cycle. The status is a worst-of rollup across them, so a row
   * carrying any is one the twin set rather than a person.
   */
  derived_from_tests?: string[];
  created_at: string;
  updated_at: string;
}

export interface AssessmentUpsertIn {
  status?: AssessmentStatus | null;
  current_ml?: number | null;
  target_ml?: number | null;
  evidence_summary?: string | null;
  gaps?: string | null;
  recommendations?: string | null;
  notes?: string | null;
  workflow_pattern?: WorkflowPattern | null;
  // P268: set the preparer/approver via the UserPicker.
  assignee_user_id?: string | null;
  approver_user_id?: string | null;
  /** C3 -- which Engine run set this judgement. */
  derived_from_run_id?: string | null;
  /** Sent as null cannot be told from not-sent under the partial-update
   *  convention, so clearing the marker takes its own flag. */
  clear_derived_from_run?: boolean;
}

// ── P268: the unified "my work" feed + user picker ──────────────────
/** How to complete a work item from the inbox, when it can be. */
export interface WorkAction {
  label: string;
  /** Round 9 G -- the same verb, composable in the reader's language. */
  label_key?: string | null;
  label_params?: Record<string, string> | null;
  method: string;
  href: string;
  body: Record<string, unknown>;
  /** Body fields to fill from the SESSION, as [nameField, emailField].
   *  Empty when the endpoint takes no signature in its body. Not a
   *  boolean: the sign-off endpoints want `name`/`email` and the
   *  challenge endpoints want `reviewer_name`/`reviewer_email`. */
  signature_fields: string[];
  /** The body field a free-text note goes into, or null when this
   *  endpoint records none. The comment box is offered only where what
   *  is typed is actually kept. */
  note_field?: string | null;
  /** D9: who holds it after this act, or null when the act finishes
   *  the ceremony. Every toast read "Signed: {item}" and stopped, so
   *  somebody who had just signed could not tell whether it was done. */
  next_role?: string | null;
  next_role_unnotified?: boolean;
  /** Round 14 D5: the next signer is an owner nobody recorded. */
  next_owner_missing?: boolean;
  /** Round 18 N11 -- the person the next signature is with, when known. */
  next_holder_name?: string | null;
  /** D7: is this a SIGNATURE, or some other act? The confirm read
   *  "Sign as Record second-line challenge", which both garbled the
   *  sentence and called a reviewer's challenge a signature. */
  is_signature?: boolean;
}

/** One line of the decision a signer is shown before signing (B1). */
export interface WorkSummaryLine {
  label_key: string;
  value: string;
  /** A3 -- true when the value is one of OUR closed vocabularies and
   *  should be read through the catalogue. A rationale or a name is
   *  the bank's own words and must not be rewritten. */
  is_term?: boolean;
  /** Round 14 D3 -- a sentence the catalogue writes, from these. */
  value_key?: string | null;
  params?: Record<string, string> | null;
}

export interface WorkItem {
  /** A1 -- whose record this is, where it belongs to somebody. */
  owner_name?: string | null;
  /** Round 18 N10 -- who holds the awaited leg where it is not the
   *  owner's own: the one holder of its seat, else null. */
  awaiting_name?: string | null;
  /** Round 19 G42 -- how many hold the awaited leg; 0 is "nobody". */
  awaiting_holders?: number | null;
  /** "mine" — the item names you. "queue" — you can act on it only
   *  because you share a queue with whoever it belongs to. */
  ownership: string;
  // P273.4: "ceremony" = a governance dual-sign-off hand-off.
  // C4: "cycle" -- the open assessment cycle, absent from the inbox
  // entirely though it is the one item with a real regulator-facing
  // deadline and the largest piece of work behind it.
  entity_type:
    | "cap"
    | "assessment"
    | "attestation"
    | "ceremony"
    | "cycle"
    | "rfi"
    | "change_finding"
    // Round 20 U1 -- a critical finding reached a service the reader answers for.
    | "critical_finding";
  id: string;
  title: string;
  subtitle: string;
  /**
   * G1: the server composes this sentence, so it arrives in English
   * whatever language the reader is in. When a key is present the
   * client renders it instead; `subtitle` stays the fallback.
   */
  subtitle_key?: string | null;
  subtitle_params?: Record<string, unknown> | null;
  /** Round 16 I13: a title the server composed, as a key. */
  title_key?: string | null;
  my_role: string;
  /** B3 -- whether the seat this awaits is the viewer's own. */
  awaiting_you?: boolean;
  /** B2 -- what this ceremony belongs to, and when it was raised. */
  group_label?: string | null;
  raised_at?: string | null;
  workflow_state: string;
  due_date: string | null;
  updated_at: string;
  deep_link: string;
  action?: WorkAction | null;
  /** B1: what is being signed, in words, before the signature. */
  summary?: WorkSummaryLine[];
  commits_to_key?: string | null;
  challenge_note?: string | null;
  /** F1 -- the signature already on this decision, named and dated. */
  signed_so_far?: WorkPriorSignature[];
}

/** One signature already recorded on a decision awaiting another. */
export interface WorkPriorSignature {
  role: string;
  name?: string | null;
  at?: string | null;
  note?: string | null;
}

export interface MyWork {
  items: WorkItem[];
  total: number;
  /** B4: how many of those need THIS person. The badge counts this,
   *  not the total -- a count that overstates the ask is one people
   *  learn to ignore. */
  needs_you?: number;
  /** Round 20 Y1: the internal audit seat -- no queue, and the page says why. */
  reads_only?: boolean;
  /** B4: what you signed that now sits with someone else. */
  waiting_on?: {
    title: string;
    awaiting_role: string;
    since?: string | null;
    /** F4: who holds that seat, where the record names exactly one. */
    awaiting_name?: string | null;
    awaiting_holders?: number;
    /** Round 14 D5: an owner seat with no owner recorded. */
    owner_missing?: boolean;
    /** Round 16 N3: comes to the viewer once that seat has signed. */
    coming_to_you?: boolean;
    /** Round 16 I11: the title as a key and parameters, where composed. */
    title_key?: string | null;
    title_params?: Record<string, unknown> | null;
  }[];
}

export interface UserOption {
  id: string;
  display_name: string;
  email: string | null;
}

export interface UsersSearch {
  users: UserOption[];
}

// ── P270: deployment license status ─────────────────────────────────
export interface LicenseStatus {
  state: "active" | "grace" | "expired" | "missing" | "invalid" | "revoked";
  customer_id: string | null;
  customer_name: string | null;
  expires_at: number | null;
  grace_days: number | null;
  grace_days_remaining: number | null;
  error_message: string | null;
  // null == the JWT carried no `modules` claim (back-compat: all licensed).
  modules: string[] | null;
  deployment_id: string | null;
  // P271: the JWT jti (revocation target) + the last online-sync epoch
  // (null until a sync runs / when no management plane is configured).
  license_id?: string | null;
  last_synced_at?: number | null;
  // Whether the deployment-license gate is enforcing. When false, the SPA
  // treats the estate as fully open regardless of state.
  gate_enabled: boolean;
}

export interface AssessmentTransitionIn {
  comment?: string | null;
}

export interface EvidenceArtefactIn {
  artefact_id?: string;
  uri?: string;
  content_hash?: string | null;
  content_type?: string | null;
  filename?: string | null;
  size_bytes?: number | null;
  notes?: string | null;
  /**
   * P22.1 (#522): transient input used ONLY by the auto-dispatch
   * Marsad Engine analyser when the cycle has
   * `auto_analyse_on_evidence_attach` enabled. Not stored on the
   * artefact; falls back to `notes` when absent. When neither is
   * set the auto-dispatch skips silently.
   */
  evidence_text?: string | null;
}

export interface EvidenceArtefact {
  link_id: string;
  artefact_id: string;
  uri: string;
  content_hash: string | null;
  content_type: string | null;
  filename: string | null;
  size_bytes: number | null;
  uploader_user_id: string | null;
  uploader_email: string | null;
  notes: string | null;
  linked_at: string;
  /**
   * P21.1 (#520): freshness-probe state populated by the
   * evidence_freshness_probe connector. `null` on artefacts the
   * scheduler hasn't reached yet (or when no probe connector is
   * configured). `last_probe_status` is one of "fresh" / "failed"
   * / "pending". `last_probe_error` is a short string when status
   * is "failed".
   */
  last_probed_at: string | null;
  last_probe_status: "fresh" | "failed" | "pending" | null;
  last_probe_error: string | null;
  /** Round 20 Y4 -- the artefact's date and what it is the date of. */
  dated_on?: string | null;
  date_basis?: "retrieved" | "uploaded" | null;
  older_than_cycle?: boolean;
}

/**
 * P75: GET /v1/evidence/{id} detail shape -- richer than
 * EvidenceArtefact (the per-cycle list row). Walks the global
 * link graph for every assessment + finding that references the
 * artefact.
 */
export interface EvidenceLinkedAssessment {
  link_id: string;
  assessment_id: string;
  framework_code: string;
  framework_control_id: string;
  cycle_id: string;
  status: string;
  current_ml: number | null;
  linked_at: string;
}

export interface EvidenceLinkedFinding {
  link_id: string;
  finding_id: string;
  title: string;
  severity: string;
  status: string;
  linked_at: string;
}

export interface EvidenceArtefactDetail {
  artefact_id: string;
  uri: string;
  content_hash: string | null;
  content_type: string | null;
  filename: string | null;
  /** Where a connector found the file in its library; '/' is the top. */
  folder_path?: string | null;
  size_bytes: number | null;
  uploader_user_id: string | null;
  uploader_email: string | null;
  notes: string | null;
  retrieved_at: string | null;
  /** Round 20 Y4 -- the date given where no retrieval is recorded. */
  uploaded_at?: string | null;
  last_probed_at: string | null;
  last_probe_status: "fresh" | "failed" | "pending" | null;
  last_probe_error: string | null;
  source_system_id: string | null;
  external_ref: string | null;
  linked_assessments: EvidenceLinkedAssessment[];
  linked_findings: EvidenceLinkedFinding[];
}

export interface AnalyseControlIn {
  control_id: string;
  evidence_text: string;
  source_artefact_id?: string | null;
}

export interface AnalyserRun {
  id: string;
  framework_code: string;
  cycle_id: string;
  triggered_by_user_id: string;
  source_artefact_id: string | null;
  /** Round 15 A11/A12 -- which control, and who queued it. */
  control_id?: string | null;
  control_natural_id?: string | null;
  triggered_by_name?: string | null;
  status: AnalyserStatus;
  mode: AnalyserMode;
  auto_dispatched: boolean;
  controls_total: number | null;
  controls_graded: number | null;
  model: string | null;
  prompt_version: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_estimate_usd: number | null;
  started_at: string | null;
  finished_at: string | null;
  error_detail: string | null;
  output_payload: Record<string, unknown> | null;
  // P61.1 (#566): retry linkage. NULL on first-attempt runs;
  // populated on retries with the parent failed run's id.
  retry_of_run_id: string | null;
  retry_attempt: number;
  /** Round 16 A21 -- why a failed run failed; null otherwise. Optional
   * for tolerant reading of older payloads and inline test mocks. */
  failure_cause?:
    | "never_started"
    | "stopped"
    | "cancelled"
    | "engine_no_answer"
    /* Round 19 A43 -- the Engine call failed. */
    | "engine_unavailable"
    | "unreadable"
    | null;
  /** Round 16 A21 -- the reader dispatched this run. */
  dispatched_by_me?: boolean;
  created_at: string;
  updated_at: string;
}

export interface AnalyseBatchIn {
  evidence_text: string;
  max_controls?: number;
  source_artefact_id?: string | null;
}

// -- Scenario library (P12) -----------------------------------------

export type ScenarioArea =
  "cyber_technology" | "operational_third_party" | "physical_environmental";
export type ScenarioSeverity = "lost" | "impaired" | "partial" | "none";
export type ScenarioImpactTargetKind =
  "ibs_service" | "service" | "product" | "configuration_item";

export interface ScenarioTemplate {
  code: string;
  name: string;
  description: string | null;
  area: ScenarioArea;
  category: string | null;
  severity_default: ScenarioSeverity;
  narrative_long: string | null;
  regulator_alignment: string | null;
  display_order: number;
  suggested_targets: Record<string, unknown>[];
}

export interface ScenarioImpactRow {
  id: string;
  target_kind: ScenarioImpactTargetKind;
  target_id: string;
  target_name: string | null;
  severity: ScenarioSeverity | null;
  notes: string | null;
  /** P418 -- what the source states about time and capacity. Optional
   *  on the type because older fixtures carry none. */
  onset_hours?: number;
  duration_basis?: "stated" | "until_recovered" | "unstated";
  duration_hours?: number | null;
  duration_hours_max?: number | null;
  capacity_loss_pct?: number | null;
  /** Round 16 D18 -- whose the duration is. */
  duration_set_by?: string | null;
  duration_set_at?: string | null;
  duration_source_label?: string | null;
  duration_source_basis?: string | null;
  duration_source_hours?: number | null;
  duration_source_hours_max?: number | null;
}

/** ROUND 15 D14 -- set or clear what a scenario states about a target.
 *  Absent leaves a field; null clears it. */
export interface ScenarioImpactPatch {
  duration_basis?: "stated" | "until_recovered" | "unstated";
  duration_hours?: number | null;
  duration_hours_max?: number | null;
  capacity_loss_pct?: number | null;
  notes?: string | null;
}

/** ROUND 15 D12 -- the composite a member scenario is part of. */
export interface ScenarioMemberOf {
  id: string;
  code: string;
  name: string;
}

/** ROUND 15 D12 -- what removing a member left behind. */
export interface ScenarioMemberRemoved {
  member_scenario_id: string;
  still_member_of: ScenarioMemberOf[];
  returned_to_library: boolean;
}

// ── War-room: impact-tolerance verdict (P157) ─────────────────────────

/** P184: live telemetry -- one current-health row per service. */
export interface ServiceHealth {
  service_id: string;
  service_name: string;
  service_code: string | null;
  /** C5: `no_data` -- nothing has ever sampled this service. Distinct
   *  from `unknown`, which means a reading went stale. */
  status: "healthy" | "degraded" | "down" | "unknown" | "no_data";
  availability_pct: number | null;
  latency_ms: number | null;
  error_rate_pct: number | null;
  /** C5: null when never sampled. A fabricated timestamp on a service
   *  nobody watches is how a page comes to look like coverage. */
  observed_at: string | null;
  sample_count: number;
  /** P185: 'external' when a real monitor pushed it, else 'synthetic'.
   *  C5: 'none' when there is no sample at all. */
  source?: "external" | "synthetic" | "none";
  /** P186: recent availability points (oldest->newest) for the sparkline. */
  recent?: number[];
  /** P227: recent latency (ms) + error-rate (%) sparkline series. */
  recent_latency?: number[];
  recent_error?: number[];
  /** P226: true when observed_at is older than the staleness TTL -- the
   * status above is then "unknown" (no current signal). */
  is_stale?: boolean;
  /** P226: the TTL (seconds) the row was judged stale against. */
  stale_after_seconds?: number;
  /** F2: business services this one supports that carry an open
   *  critical finding. Context beside the reading, never a change to
   *  it -- the reading is about the service, the incident is about the
   *  IBS, and the page used to say only the first. */
  incident_ibs?: string[];
  /** Round 20 U5 -- the worst open critical or high finding on this
   *  service itself or on a host it runs on, named first on the row. */
  own_alert_severity?: string | null;
  own_alert_since?: string | null;
  own_alert_on_host?: boolean;
}

/** P227: one point in a service's telemetry history (the drill-in chart). */
export interface ServiceHistoryPoint {
  observed_at: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  availability_pct: number | null;
  latency_ms: number | null;
  error_rate_pct: number | null;
}

export interface ServiceHistory {
  service_id: string;
  service_name: string;
  points: ServiceHistoryPoint[];
}

export interface TelemetryReport {
  total: number;
  healthy: number;
  degraded: number;
  down: number;
  unknown: number;
  /** P226: how many rows are stale (counted within `unknown`). */
  stale?: number;
  rows: ServiceHealth[];
  /** C5: services nothing has ever sampled -- a gap in coverage, not
   *  in freshness. */
  no_data?: number;
  /** Round 20 U8 -- who is told when a supporting service stays down,
   *  and after how many minutes. */
  outage_notify_minutes?: number;
  outage_notify_opsres_holders?: number;
  outage_notify_admins?: number;
}

export interface TelemetrySampleResult {
  sampled: number;
  healthy: number;
  degraded: number;
  down: number;
}

/** P192/193: the recommendations "so what?" engine. */
export interface RecommendationCitation {
  kind: string;
  ref: string;
  /** The composed English. What a signed pack carries, and the
   *  fallback where a citation has no key yet. */
  detail: string;
  /** Round 9 G -- the same sentence, composable in the reader's
   *  language. */
  detail_key?: string | null;
  /** `string | number` -- the number is load-bearing: i18next selects
   *  a plural form on a numeric `count`, and a string selects none, so
   *  the key renders instead of the sentence. Arabic has six plural
   *  forms against English's two, which is the case that makes this
   *  more than a nicety. */
  detail_params?: Record<string, string | number> | null;
}

export interface Recommendation {
  id: string;
  source: "assessment_gap" | "ibs_risk" | "scenario_breach";
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  rationale: string;
  suggested_action: string;
  action_kind: "draft_cap" | "war_room" | "fragility" | "review";
  action_ref: string | null;
  action_ref_kind?: string | null;
  /** F -- the same sentence, for a reader in their own language. The
   *  server composes the English; the client cannot translate a
   *  sentence it did not build, so the key and its parameters travel
   *  alongside. */
  title_key?: string | null;
  title_params?: Record<string, string | number>;
  rationale_key?: string | null;
  rationale_params?: Record<string, string | number>;
  action_key?: string | null;
  action_params?: Record<string, string | number>;
  mvb_related: boolean;
  priority_score: number;
  signals: Record<string, number>;
  // P195: SAR annual revenue at risk (string for Decimal precision; null ==
  // not measured).
  revenue_at_risk_sar: string | null;
  citations: RecommendationCitation[];
}

export interface RecommendationsReport {
  generated_at: string;
  total: number;
  by_source: Record<string, number>;
  by_severity: Record<string, number>;
  recommendations: Recommendation[];
}

/** P267 -- resilience maturity readiness (live suggested read). */
export interface ReadinessDimension {
  code: string;
  name: string;
  weight: number;
  anchor: string;
  suggested_score: number;
  /** Round 17 B14 -- false when no internal evidence was found. */
  suggestion_offered?: boolean;
  evidence_present: string[];
  evidence_absent: string[];
  external_only: string[];
  horizon: string;
  gap_route: string | null;
  /** Round 9 G -- the same fields as catalogue keys. The English above
   *  stays: the signed maturity pack carries it, and a signed artefact
   *  is evidence in one language by design. */
  name_key?: string;
  anchor_key?: string;
  evidence_present_keys?: string[];
  evidence_absent_keys?: string[];
  external_only_keys?: string[];
}

export interface Readiness {
  generated_at: string;
  /** Round 18 B18 -- null, with not_suggested_reason, when nothing is
   *  admitted: no maturity is suggested from rows nobody judged. */
  suggested_index: number | null;
  tier_code: string | null;
  tier_label: string | null;
  floor_applied: boolean;
  not_suggested_reason?: string | null;
  dimensions: ReadinessDimension[];
}

/** P336 -- supervisory coherence checks (the SAMA field-examiner's questions). */
export interface SupervisoryDistributionCheck {
  key: string;
  label: string;
  ok: boolean;
  /* Round 12 B1 -- did this check have anything to check? `ok` answers
     "did it fail"; an empty register does not fail and has not passed
     either, and the field exam rendered the second as the first. */
  assessed?: boolean;
  detail: string;
  /** ROUND 15 I7 -- `detail` as catalogued fragments, and their joiner. */
  detail_said?: { key: string; params?: Record<string, unknown> }[];
  detail_join?: string;
  /** D10 -- the rows in each class, addressable. */
  classes?: SupervisoryDistributionClass[];
}

export interface SupervisoryDistributionClass {
  value: string;
  count: number;
  members?: SupervisoryFlagMember[];
  truncated?: boolean;
}

export interface SupervisoryFlagMember {
  kind: string;
  id: string;
  name: string;
  /** ROUND 15 B6 -- tells two records with one name apart. */
  code?: string | null;
}

export interface SupervisoryCoherenceFlag {
  key: string;
  label: string;
  entity_kind: string;
  entity_id: string;
  entity_name: string;
  detail: string;
  /** ROUND 15 I7 -- `detail` as catalogued fragments. */
  detail_said?: { key: string; params?: Record<string, unknown> }[];
  /** D4 -- every row the flag counted, not just the one it named. */
  members?: SupervisoryFlagMember[];
  truncated?: boolean;
}

export interface SupervisoryCoverageRow {
  service_id: string;
  service_name: string;
  scenario_assessed: boolean;
  drill_covered: boolean;
  blast_sealed: boolean;
  channels_reached: number;
  ci_scenario_targets: number;
  covered: boolean;
}

export interface SupervisoryChecks {
  generated_at: string;
  distribution: SupervisoryDistributionCheck[];
  coherence: SupervisoryCoherenceFlag[];
  coverage: SupervisoryCoverageRow[];
  coverage_pct: number | null;
  checks_total: number;
  checks_flagged: number;
  /** B1 -- so nobody arrives at passes by subtraction. */
  checks_not_assessed?: number;
  /** Round 17 B14 -- false when nothing is admitted to reconcile. */
  coherence_assessed?: boolean;
  /** Round 14 D2 -- `coverage_pct` is P419's: tested operation-class
   *  pairs over the pairs the regime requires. */
  coverage_tested?: number;
  coverage_required?: number;
  coverage_operations_total?: number;
  coverage_operations_complete?: number;
}

export interface BaselineRatio {
  key: string;
  label: string;
  numerator: number;
  denominator: number;
  pct: number;
  /** Round 13 D10: the importable entity that FILLS this rung, so the
   * screen offers a route rather than only a shortfall. Null where no
   * import path exists. */
  fix_entity_kind?: string | null;
}

export interface BaselinePillar {
  key: string;
  label: string;
  pct: number;
  detail: string;
  ratios: BaselineRatio[];
  /** Round 19 I23 -- the sentence as a catalogue key and its figures. */
  detail_key?: string | null;
  detail_params?: Record<string, number> | null;
}

export interface DataBaseline {
  generated_at: string;
  pillars: BaselinePillar[];
  coherence_flag_count: number;
  baseline_pct: number;
  verdict: "established" | "partial" | "thin";
}

/** P335 -- the MARSAD Resilience Score (Score 0-100 · Tier A-E · Confidence %). */
export interface ScoreComponent {
  key: string;
  value: number | null; // 0..1, null = not measurable on this estate
  detail: string;
}

export interface ScoreSubScore {
  key: string;
  weight: number;
  value: number | null; // 0..100
  components: ScoreComponent[];
}

export interface ResilienceScore {
  generated_at: string;
  name: string;
  methodology_version: string;
  score: number | null;
  base_tier: string;
  tier: string;
  confidence: number;
  confidence_capped: boolean;
  posture: ScoreSubScore;
  exposure: ScoreSubScore;
  outcome: ScoreSubScore;
  proof: ScoreComponent[];
  posture_source: string; // "assessed" | "live"
  maturity_drift: number | null;
  inputs_hash: string;
  last_sealed_at: string | null;
  last_sealed_score: string | null;
  /** Round 17 B10 -- why no score was computed ("nothing_admitted"), or
   *  null when one was. Optional for older payloads. */
  not_scored_reason?: string | null;
  admitted_services?: number;
  /** Round 20 Y3 -- who has signed the assessment posture rests on;
   *  null when posture is live. */
  posture_signatures?: { cro: string | null; ops_res: string | null } | null;
}

/** P330 -- Basel-7 op-res readiness crosswalk (a lens over Readiness). */
export interface BaselPillarDimension {
  code: string;
  name: string;
  suggested_score: number;
  proved: string[];
  attested: string[];
  gap: string[];
}

export interface BaselPillar {
  number: number;
  key: string;
  name: string;
  intent: string;
  note: string | null;
  dimensions: BaselPillarDimension[];
  proved_count: number;
  attested_count: number;
  gap_count: number;
  total_probes: number;
  proved_ratio: number;
  status: string; // proved | strong | partial | thin
  rolled_score: number;
  below_baseline: boolean;
  gap_routes: string[];
}

export interface BaselCrosswalk {
  generated_at: string;
  pillars: BaselPillar[];
  pillars_proved: number;
  pillars_partial: number;
  pillars_thin: number;
  proved_share: number;
  overall_status: string; // ready | developing | early
}

export interface MaturityAssessmentDimension {
  score: number;
  narrative: string | null;
}

/** P267.3 -- the persisted, dual-signed self-assessment. */
export interface MaturityAssessment {
  id: string;
  version: number;
  is_active: boolean;
  workflow_state: string; // draft | cro_signed | ops_res_signed | approved
  set_index: number | null;
  set_tier: string | null;
  floor_applied: boolean;
  dimensions: Record<string, MaturityAssessmentDimension>;
  cro_signoff_name: string | null;
  cro_signoff_at: string | null;
  /** D8: the caveat the signer put beside their name. */
  cro_signoff_note?: string | null;
  ops_res_signoff_name: string | null;
  ops_res_signoff_at: string | null;
  ops_res_signoff_note?: string | null;
  created_at: string;
}

export interface ToleranceRow {
  ibs_service_id: string;
  ibs_name: string;
  mvb_flag: boolean;
  /** Round 16 L3 -- a Critical Internal Function's row. */
  is_function?: boolean;
  severity: string;
  projected_outage_hours: number;
  tolerance_rto_hours: number | null;
  binding_service: string | null;
  verdict: "within" | "at_risk" | "breached" | "unknown";
  /** P180 timed twin: effective tolerance + the breach/recovery clock. */
  impact_tolerance_hours?: number | null;
  tolerance_source?: "declared" | "rto_proxy" | "none";
  breaches_at_hours?: number | null; // null = never breaches
  recovers_at_hours?: number | null;
  headroom_hours?: number | null; // >0 slack / <0 overrun
  /** P183 recovery curve: redundancy-derived profile + cumulative
   *  service-hours lost + partial-restore milestone + points to plot. */
  recovery_profile?: "step" | "linear";
  service_hours_lost?: number;
  partial_restore_at_hours?: number | null;
  recovery_curve?: [number, number][]; // (hour, availability_pct)
  /** P187: the upstream service/vendor whose failure pulled this IBS in
   *  (null/absent = the IBS was targeted directly). */
  propagated_via?: string | null;
  /** P188: true when propagated_via is a currently down/degraded service
   *  from live telemetry (not the scenario). */
  via_live?: boolean;
  /** Round 16 M2 -- reached through a funding edge; propagated_via names the function that funds it. */
  via_funding?: boolean;
  /** P202: "declared & signed off by ..." when the tolerance came from an
   *  approved P201 declaration; null/absent for a seed / calibration value. */
  tolerance_provenance?: string | null;
  /** B1 -- which of the four tolerance states, in the shared
   *  vocabulary. `tolerance_source` says where the number came from
   *  and nothing about who signed for it. */
  tolerance_status?: string | null;
  tolerance_proposed_hours?: number | null;
  tolerance_awaiting_role?: string | null;
  /** Round 17 P4 -- since when the awaited leg has waited, and whether
   *  the service has an owner to countersign at all. */
  tolerance_awaiting_since?: string | null;
  tolerance_owner_recorded?: boolean;
  /** P203: the failed-upstream-service RTO (hours) that bounds this IBS's
   *  recovery -- it can't recover before its failed dependency does. */
  upstream_rto_hours?: number | null;
  /** P418 -- what the SCENARIO stated, beside what the model derived.
   *
   *  `stated_duration_basis` is what keeps the pair honest. Three of
   *  SAMA's mandated nine state no duration at all and a fourth says
   *  the outage lasts until the failed update is fixed, so a null
   *  duration on its own cannot say which of those a reader is looking
   *  at. `stated_duration_hours_max` above the lower bound is a stated
   *  RANGE, which the regulator wrote once ("5 to 12 hours"). */
  stated_duration_hours?: number | null;
  stated_duration_hours_max?: number | null;
  stated_duration_basis?: "stated" | "until_recovered" | "unstated" | null;
  /** Round 16 D22 -- channels still working where the projection was
   *  lowered because the scenario darkens only some of them. */
  still_working_channels?: string[];
  /** Round 16 D20 -- the floor (onset plus the stated upper bound)
   *  and the member's onset. */
  stated_envelope_hours?: number | null;
  stated_onset_hours?: number | null;
  /** Round 16 D18 -- whose the stated figure is. */
  stated_set_by?: string | null;
  stated_set_at?: string | null;
  stated_source_label?: string | null;
  stated_source_basis?: string | null;
  stated_source_hours?: number | null;
  stated_source_hours_max?: number | null;
  /** The worst STATED capacity loss among the impacts reaching this IBS.
   *  Null means none was stated -- NOT that the loss is zero. */
  capacity_loss_pct?: number | null;
  /** Round 14 D8: each stated figure under the target it was stated for. */
  stated_figures?: StatedFigure[];
  /** True when the projected outage was RAISED to meet a stated
   *  duration: the event outlasts the bank's own recovery model. */
  outage_floored_by_statement?: boolean;
  /** P417 -- the clock the tolerance was read against, and the line the
   *  bank drew inside it.
   *
   *  `active_window` is the declared window tightening this tolerance
   *  right now; `base_tolerance_hours` is what stood before it, so a
   *  reader sees what the window DID rather than only its result.
   *  `limit_hours` is the declared operational risk limit -- where one
   *  exists, `at_risk` means crossing it rather than a fraction chosen
   *  once for every business service. */
  active_window?: string | null;
  base_tolerance_hours?: number | null;
  limit_hours?: number | null;
  next_window?: string | null;
  next_window_on?: string | null;
}

export interface ToleranceReport {
  scenario_id: string;
  as_of: string;
  assessed: number;
  /** Round 16 L3 -- functions counted apart from the IBS figures. */
  functions_assessed?: number;
  functions_breached?: number;
  /** Round 20 P24 -- of those functions, the ones under assessment. */
  functions_candidates_assessed?: number;
  /** Round 17 P1 -- of the services counted, those still under assessment. */
  candidates_assessed?: number;
  candidates_breached?: number;
  candidates_mvb_breached?: number;
  /** Stitching review 5.4 -- governed, counted once on the server over
   *  the services: among the breached and among the assessed. */
  governed_breached?: number;
  governed_assessed?: number;
  within: number;
  at_risk: number;
  breached: number;
  mvb_breached: number;
  /** P180: soonest breach + total overrun across the breached IBSes. */
  soonest_breach_hours?: number | null;
  total_overrun_hours?: number;
  /** P183: cumulative service-hours lost across all impacted IBSes. */
  total_service_hours_lost?: number;
  /** P188: whether live telemetry was folded into the baseline + how many
   *  rows current live conditions drove. */
  live_baseline_applied?: boolean;
  live_impacted_count?: number;
  /** Round 13 G1: `assessed` counts the IBSes this report REACHED, which
   *  is the number the headline divides by. `declared_total` is how many
   *  declared a tolerance at all, and `not_reached` names the ones this
   *  scenario never touched -- they carry no verdict in either
   *  direction, and saying nothing about them read as "within". */
  declared_total?: number;
  not_reached?: UnreachedIBS[];
  /** Round 14 D1: targets that reached no business service. */
  empty_targets?: EmptyTarget[];
  rows: ToleranceRow[];
}

/** Round 14 D8: what a scenario stated about one target. */
export interface StatedFigure {
  target_kind: string;
  target_name: string;
  duration_basis: "stated" | "until_recovered" | "unstated";
  duration_hours: number | null;
  duration_hours_max: number | null;
  capacity_loss_pct: number | null;
  /** Round 18 D25 -- the bank's own figure, not the source's. */
  set_by_bank?: boolean;
}

/** Round 14 D1: a scenario target that reached no business service. */
export interface EmptyTarget {
  entity_kind: string;
  entity_name: string;
}

/** Round 13 G1: an IBS with a declared tolerance that a war-room report
 *  did not reach. */
export interface UnreachedIBS {
  ibs_service_id: string;
  ibs_name: string;
  mvb_flag: boolean;
  impact_tolerance_hours: number;
}

/** P160: war-room "cheapest fix" -- a single-service hardening that
 *  clears breaches. candidates[0] is the recommended (highest-impact)
 *  fix. */
export interface FixCandidate {
  service_id: string;
  service_name: string;
  current_rto_hours: number;
  target_rto_hours: number;
  rto_delta_hours: number;
  ibs_cleared: number;
  mvb_cleared: number;
  cleared_ibs_names: string[];
  /** P178: SAR cost to harden this service (null == not costed). */
  hardening_cost_sar?: string | null;
}

export interface FixPlanStep {
  service_id: string;
  service_name: string;
  current_rto_hours: number;
  target_rto_hours: number;
  rto_delta_hours: number;
  newly_cleared_ibs_names: string[];
  cumulative_ibs_cleared: number;
  cumulative_mvb_cleared: number;
  /** P178 follow-on: per-step SAR hardening cost (null = uncosted). */
  hardening_cost_sar?: string | null;
}

export interface FixPlan {
  steps: FixPlanStep[];
  total_ibs_cleared: number;
  total_mvb_cleared: number;
  /** Round 19 P16 -- of them, critical internal functions. */
  total_mvb_functions_cleared?: number;
  total_rto_delta_hours: number;
  /** P178: total SAR cost of the plan (null when any step is uncosted). */
  total_cost_sar?: string | null;
}

/** P176: the most-MVB-clearing service set affordable within an RTO budget. */
export interface BudgetedFixPlan {
  budget_hours: number;
  chosen_service_names: string[];
  ibs_cleared: number;
  mvb_cleared: number;
  total_spend_hours: number;
  unaffordable_breaches: number;
  /** P178: "hours" (RTO-delta) or "sar" (hardening cost). */
  cost_basis: "hours" | "sar";
  /** P178: total SAR spend when the budget is SAR-based. */
  total_spend_sar?: string | null;
}

export interface CheapestFixReport {
  scenario_id: string;
  as_of: string;
  breached: number;
  mvb_breached: number;
  /** Round 17 P3 -- functions that breach. The plan's cleared count
   *  includes them, so the war room adds them to `breached`. */
  functions_breached?: number;
  candidates: FixCandidate[];
  plan: FixPlan;
  budgeted?: BudgetedFixPlan | null;
}

/** P162: fragility radar -- scenario-free concentration-risk scoring of
 *  upstream nodes (services + vendors). */
export interface FragilityNode {
  node_kind: string;
  node_id: string;
  name: string;
  blast_reach: number;
  mvb_downstream: number;
  open_findings: number;
  max_severity: string | null;
  fragility_score: number;
  signals: Record<string, number>;
}

export interface ExposedIBS {
  ibs_id: string;
  name: string;
  mvb_flag: boolean;
  supporting_count: number;
  binding_rto_hours: number | null;
  open_findings: number;
  max_severity: string | null;
  exposure_score: number;
  signals: Record<string, number>;
  /** B1 -- the DECLARED tolerance and what stands behind it. Separate
   *  from `binding_rto_hours`, which is the tightest supporting RTO:
   *  the radar printed that under a "Tolerance" heading, so an IBS
   *  declared at 2h read 24h. */
  tolerance_hours?: number | null;
  tolerance_status?: string;
  tolerance_proposed_hours?: number | null;
  tolerance_awaiting_role?: string | null;
  /** Round 17 P4 -- since when the awaited leg has waited, and whether
   *  the service has an owner to countersign at all. */
  tolerance_awaiting_since?: string | null;
  tolerance_owner_recorded?: boolean;
}

export interface FragilityReport {
  generated_at: string;
  assessed: number;
  degraded: number;
  /** B7 -- the same count for the exposed list. */
  exposed_degraded?: number;
  watchlist: FragilityNode[];
  exposed: ExposedIBS[];
}

export interface ScenarioSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  area: ScenarioArea;
  category: string | null;
  severity_default: ScenarioSeverity;
  template_code: string | null;
  is_active: boolean;
  /** ROUND 15 D12 -- empty for a scenario that is nobody's member. */
  member_of?: ScenarioMemberOf[];
  impact_count: number;
  impact_counts_by_kind: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface ScenarioDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  area: ScenarioArea;
  category: string | null;
  severity_default: ScenarioSeverity;
  narrative_long: string | null;
  regulator_alignment: string | null;
  template_code: string | null;
  is_active: boolean;
  member_of?: ScenarioMemberOf[];
  /** P418 -- the severe-but-plausible record. Optional on the type so
   *  an older payload still reads. */
  scenario_class?: string;
  plausibility_basis?: string | null;
  basis_owner_email?: string | null;
  basis_approved_by_email?: string | null;
  basis_approved_at?: string | null;
  basis_next_review?: string | null;
  /** Round 18 R8 -- the regulator's asks this scenario answers. */
  regime_refs?: {
    id: string;
    regime_code: string;
    citation: string | null;
    created_at: string;
  }[];
  impacts: ScenarioImpactRow[];
  created_at: string;
  updated_at: string;
}

// ── Customer journeys (P16.2 / #485) ──────────────────────────────
//
// Mirrors backend/twin/journey_router.py.

export interface JourneyStep {
  id: string;
  step_number: number;
  name: string;
  description: string | null;
  /** P28 entity tags: which node in the twin this step runs through.
   *  All null where the step carries no tag -- a mapping gap, which the
   *  3D journey reports rather than fills in. */
  service_id?: string | null;
  capability_id?: string | null;
  vendor_id?: string | null;
}

export interface JourneySummary {
  id: string;
  ibs_service_id: string;
  ibs_service_code: string;
  ibs_service_name: string;
  channel_id: string;
  channel_code: string;
  channel_name: string;
  step_count: number;
}

export interface JourneyDetail extends JourneySummary {
  description: string | null;
  steps: JourneyStep[];
}

// ── Board snapshot (P17.1 / #458) ────────────────────────────────
//
// Mirrors backend/dossier/board_composer.py.
// JSON shape is the contract; PDF endpoint renders the same payload.

/* Round 12 B1 -- a fourth state. On a tenant with no tolerances, no
   admitted services and no telemetry the board headlined GREEN, "No
   open finding on any IBS reaches amber or red". True, and a claim
   about an estate nobody had looked at. `not_assessed` is the absence,
   said out loud; every consumer of a Rag has to decide what to do with
   it, which is the point -- an unhandled fourth value falling through
   to green is the defect it removes. */
export type Rag = "red" | "amber" | "green" | "not_assessed";

export interface BoardSnapshotHeadline {
  overall_rag: Rag;
  /** D4: why the board is that colour, in the board's own terms. */
  overall_rag_because?: string;
  /** Round 9 G -- the same sentence, composable in the reader's
   *  language. The English stays for the signed PDF. */
  overall_rag_because_key?: string;
  overall_rag_because_params?: Record<string, string | number>;
  /**
   * D4: the SEALED resilience counts -- the only headline figures that
   * genuinely replay at an as_of. Everything else here is
   * current-state.
   */
  ibs_resilience_red?: number;
  ibs_resilience_amber?: number;
  /** C4: how many IBSes had a SEALED state at this moment. Zero red
   *  because nothing was sealed is a different fact from zero red
   *  because everything was green. */
  ibs_resilience_sealed?: number;
  ibs_count: number;
  ibs_impacted: number;
  products_at_risk_total: number;
  /** Decimal-as-string per Pydantic v2 convention. */
  revenue_at_risk_annual: string;
  revenue_at_risk_daily: string;
  revenue_measured_count: number;
  revenue_unmeasured_count: number;
  /**
   * P24.1 (#535): revenue-weighted overall RAG. Uses a severity-
   * weighted average across the per-outcome RAGs, weighted by each
   * outcome's annual SAR at risk. Falls back to the worst-of-4
   * value when total weight = 0 (no measured products) so this
   * field is never null.
   */
  overall_rag_revenue_weighted: Rag;
  /** Decimal-as-string. `"0"` when no measured products are in scope. */
  overall_rag_revenue_weight_total: string;
  /**
   * P26 (#545): MVB intersection rollup lifted off the cascade
   * summary the board composer already runs. Direct-impact set
   * only; bank-wide denominator always reported even on empty
   * cascade. `mvb_impacted_share` is `impacted/total` as a 4dp
   * float, `null` when `mvb_total_count = 0` (no MVB-flagged
   * IBSes exist in the bank).
   */
  mvb_impacted_count: number;
  mvb_total_count: number;
  mvb_impacted_share: number | null;
  /** P429.6 -- Critical Internal Functions, counted apart from `ibs_count`. */
  cif_count?: number;
  cif_impacted?: number;
  /** Round 20 P19 -- functions under assessment, and those with an open
   *  tolerance-breach finding. */
  cif_under_assessment?: number;
  cif_under_assessment_breach_findings?: number;
}

export interface BoardOutcomeRag {
  outcome: string;
  rag: Rag;
  ibs_count: number;
  ibs_impacted: number;
  /** P429.6 -- Critical Internal Functions in this outcome, counted apart. */
  cif_count?: number;
  cif_impacted?: number;
  /** Round 18 P5 -- of the counts above, how many are admitted. */
  ibs_admitted?: number | null;
  cif_admitted?: number | null;
  /** Round 20 P19 -- what the quoted finding sits on. */
  headline_finding_on_status?: string | null;
  headline_finding_on_function?: boolean | null;
  /** Round 20 P19 -- false when nothing sits in this outcome. */
  has_members?: boolean;
  headline_finding_title: string | null;
  headline_finding_severity: string | null;
  /** C8: so the board's named finding is reachable. */
  headline_finding_id?: string | null;
  /**
   * P24.1 (#535): annual SAR-at-risk summed across impacted products
   * allocated to IBSes in this outcome. Decimal-as-string.
   */
  revenue_at_risk: string;
  /**
   * P24.1 (#535): this outcome's fraction of the total weighted basis
   * (4dp float, 0.0 to 1.0). `null` when the total is 0.
   */
  revenue_share: number | null;
}

export interface BoardHotSpot {
  entity_kind: string;
  entity_id: string;
  entity_name: string;
  outcome_category: string | null;
  severity: string;
  open_finding_count: number;
  annual_revenue_at_risk_via_products: string | null;
  journey_count: number;
  /** C4: when the finding behind this row was raised, so the board can
   *  keep the promise its own as-of banner makes. */
  headline_finding_raised_at?: string | null;
}

export interface BoardMvb {
  declaration_id: string | null;
  declaration_name: string | null;
  readiness_state: string | null;
  review_due_in_days: number | null;
  ibs_count_scoped: number;
  /** Round 17 P1 -- of the frozen set, the admitted services. */
  ibs_count_scoped_admitted?: number | null;
  dual_signoff_complete: boolean;
  /** Round 20 G43 */
  review_before_effective?: boolean;
}

export interface BoardFrameworkAssessments {
  cycles_open: number;
  assessments_total: number;
  assessments_approved: number;
  assessments_in_review: number;
  assessments_draft: number;
  approved_pct: number;
}

export interface BoardResilienceTrendDay {
  day: string;
  counts: Record<string, number>;
}

export interface BoardModuleStatus {
  twin: boolean;
  framework: boolean;
  mvb: boolean;
  journeys: boolean;
}

export interface BoardToleranceHeadroom {
  assessed: number;
  within: number;
  at_risk: number;
  breached: number;
  mvb_breached: number;
  tightest_headroom_hours: number | null;
  tightest_headroom_ibs: string | null;
  worst_overrun_hours: number | null;
  worst_overrun_ibs: string | null;
  /** P429.3: Critical Internal Functions, counted beside the IBS figures. */
  cif_assessed?: number;
  /** Round 17 P1 -- services still under assessment, counted apart. */
  candidates_assessed?: number;
  candidates_breached?: number;
  /** Round 18 P5 -- every service under assessment, tolerance or not. */
  candidates_total?: number;
  cif_breached?: number;
}

/** P428.3 -- one group as the board reads it (worst member's colour). */
export interface BoardGroupLine {
  code: string;
  name: string;
  is_reserved: boolean;
  resilience: string | null;
  members: number;
  breached_now: number;
  mvb_members: number;
  derived_tolerance_hours: number | null;
  derived_tolerance_from: string | null;
  owner_name: string | null;
}

export interface BoardSnapshot {
  as_of: string;
  headline: BoardSnapshotHeadline;
  outcome_rag: BoardOutcomeRag[];
  hot_spots: BoardHotSpot[];
  mvb: BoardMvb;
  framework_assessments: BoardFrameworkAssessments;
  resilience_trend_30d: BoardResilienceTrendDay[];
  module_status: BoardModuleStatus;
  /** P397 / vendor question 5.3 -- consumption and gap to tolerance. */
  tolerance_headroom: BoardToleranceHeadroom | null;
  /** P428.3 -- the group grain; optional so older payloads still render. */
  groups?: BoardGroupLine[];
  ungrouped_admitted?: number;
}

// ── Audit events (P4.3 backend, P39 frontend) ──────────────────────

export interface AuditEvent {
  id: string;
  actor_user_id: string | null;
  actor_username: string | null;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  /** D1 -- what the row is about, in words, read from the row the
   *  audit points at rather than guessed from its payload. */
  entity_label?: string | null;
  /** Round 14 G -- the parent record "View source" opens, where the
   *  audited row is a run of something (a connector run's connector,
   *  an analyser run's service). */
  source_id?: string | null;
  payload: Record<string, unknown>;
  ip_addr: string | null;
  user_agent: string | null;
  created_at: string;
}

/**
 * ROUND 15 E9 -- an import that produced or changed the filtered record,
 * read from the batch's own retained rows. The importer writes one audit
 * event per committed batch; this is where the per-record answer lives.
 */
export interface ImportTouch {
  batch_id: string;
  entity_kind: string;
  filename: string | null;
  action: string | null;
  row_number: number;
  status: string;
  committed_at: string | null;
  committed_by_username: string | null;
}

export interface AuditEventListResponse {
  events: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
  /** Populated only when the list is filtered to one `entity_id`. */
  imports?: ImportTouch[];
}

export interface AuditEventQuery {
  event_type_prefix?: string;
  event_type?: string;
  entity_type?: string;
  entity_id?: string;
  actor_user_id?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

// P40.1: connectors admin grid
// P60.1 (#551): config JSONB field added
export interface ConnectorRow {
  id: string;
  kind: string;
  label: string;
  source_system_code: string;
  source_system_label: string;
  enabled: boolean;
  cadence_seconds: number | null;
  config: Record<string, unknown> | null;
  last_succeeded_at: string | null;
  last_succeeded_until_watermark: string | null;
  error_streak: number;
  last_error_message: string | null;
  /** D5: the last ATTEMPT and how it went, distinct from last success. */
  last_run_at?: string | null;
  last_run_status?: string | null;
  /** D2: when the settings last changed, so a repaired connector can
   *  say "run it again" rather than showing the new config beside the
   *  old error. */
  settings_changed_at?: string | null;
}

export interface ConnectorListResponse {
  connectors: ConnectorRow[];
}

/** P213: one credential/config field's presence (never the value). */
export interface ConnectorCredentialField {
  name: string;
  secret: boolean;
  label: string;
  required: boolean;
  status: "stored" | "env" | "not_set";
}

export interface ConnectorCredentialsResponse {
  connector_id: string;
  kind: string;
  encryption_configured: boolean;
  fields: ConnectorCredentialField[];
}

/** P213.3: result of a connector connection test (dry first fetch). */
export interface ConnectorTestResult {
  ok: boolean;
  latency_ms: number;
  records_in_first_batch: number | null;
  error: string | null;
}

export interface ConnectorRunRow {
  id: string;
  connector_id: string;
  trigger_kind: string;
  status: string;
  since_watermark: string | null;
  until_watermark: string | null;
  started_at: string | null;
  completed_at: string | null;
  row_total: number;
  row_inserted: number;
  row_updated: number;
  row_skipped: number;
  error_count: number;
  error_message: string | null;
  /** Skips by the reason the adapter gave (P427). Absent on old rows. */
  skipped_by_reason?: Record<string, number>;
}

/** A connector run as it happens (Alembic 0206). */
export interface LiveConnectorRun {
  id: string;
  connector_id: string;
  connector_kind: string | null;
  connector_label: string | null;
  phase: "queued" | "running";
  trigger_kind: string;
  queued_at: string;
  started_at: string | null;
  updated_at: string;
  batches: number;
  row_total: number;
  row_inserted: number;
  row_updated: number;
  row_skipped: number;
  /** Silent too long to be called running; said, not hidden. */
  stale: boolean;
}

export interface ConnectorRunQueuedOut {
  connector_id: string;
  connector_kind: string;
  triggered: boolean;
}

// P41.1: admin settings read
export interface SettingsEnvRow {
  key: string;
  group: string;
  description: string;
  is_set: boolean;
  is_secret: boolean;
  value: string | null;
}

export interface SettingsAppRow {
  id: string;
  key: string;
  category: string;
  data_type: string;
  description: string | null;
  value: unknown;
  default_value: unknown;
  updated_at: string;
  updated_by_username?: string | null;
}

export interface AppSettingPatchIn {
  value: unknown;
}

export interface AppSettingHistoryRow {
  captured_at: string;
  value: unknown;
  data_type: string;
  updated_by_username: string | null;
  captured_by: string;
  row_hash_hex: string;
}

/** Round 16 K12 -- the restore: typed, recorded, and whether they disagree. */
export interface RestoreRecord {
  stated: string | null;
  recorded_at: string | null;
  disagree: boolean;
}

export interface SettingsAdminResponse {
  env: SettingsEnvRow[];
  app_settings: SettingsAppRow[];
  restore?: RestoreRecord | null;
}

// ── 17. Constellation Twin (P81 + P82 + P83) ──────────────────────────

export type TwinNodeKind = "channel" | "ibs" | "service" | "vendor";

/** Round 17 M6 -- "funds": a function funding an IBS, propagation only. */
export type TwinEdgeRelation =
  "supports" | "depends_on" | "hosted_by" | "linked_to" | "funds";

export interface TwinNode {
  id: string;
  kind: TwinNodeKind;
  name: string;
  code: string | null;
  description: string | null;
  // IBS-only
  mvb_flag: boolean | null;
  /** Derived from the class since Alembic 0213: false for a State
   * service or a Critical Internal Function, which owe no delivery
   * channel. */
  customer_facing?: boolean | null;
  /** P429.6: register class; "internal" = a Critical Internal Function. */
  service_class?: "customer" | "state" | "internal" | null;
  sovereign_flag: boolean | null;
  outcome_category: string | null;
  /** SAR; serialises as a Decimal string. None when no products
   * link to this IBS, or none of the linked products carry an
   * annual_revenue value. */
  annual_revenue_sar: string | null;
  /** P408: declared maximum tolerable disruption, in hours. NULL means
   * the bank has declared none -- render the absence, never a default:
   * a breach clock produced by a rendering fallback is a supervisory
   * figure nobody set. */
  impact_tolerance_hours?: number | null;
  // Service-only
  biz_crit: string | null;
  // Vendor-only
  material_flag: boolean | null;
  open_finding_count: number;
  /** Round 17 P1 -- candidate / admitted / removed, on a register node. */
  admission_status?: string | null;
  /** Round 18 B16 -- whether anything has judged this node. With no
   *  open finding, an unjudged node is "not assessed", never "Stable".
   *  Optional: an older reply reads as before. */
  judged?: boolean;
  /** Round 19 B25 -- the latest sealed resilience state; the node's word
   *  comes from it. Absent on an older reply. */
  resilience?: "red" | "amber" | "green" | "unknown" | null;
}

export interface TwinEdge {
  from_id: string;
  from_kind: TwinNodeKind;
  to_id: string;
  to_kind: TwinNodeKind;
  relation: TwinEdgeRelation;
  /** Minutes a failure takes to cross this edge, where the bank has
   *  declared one (0182). null = not declared, and Cascade falls back
   *  to the tenant hop delay. */
  propagation_delay_minutes?: number | null;
}

export interface TwinGraph {
  /** ISO-8601 datetime. */
  as_of: string;
  nodes: TwinNode[];
  edges: TwinEdge[];
  /** Round 17 M6 -- edges a failure travels that the scene does not
   *  draw (a function's funding of an IBS). */
  propagation_edges?: TwinEdge[];
}

/** Canonical sealing reference returned by P81 blast-graph + P82
 * provenance endpoints. Lazy-genesis when hash is null and
 * prev_hash === "genesis". */
export interface ChainSeal {
  chain: string;
  entity_kind: string;
  entity_id: string;
  captured_at: string | null;
  as_of: string | null;
  hash: string | null;
  prev_hash: string;
  deriver: string | null;
  derivation_version: number | null;
}

export interface TwinBlastSummary {
  reached_total: number;
  reached_by_kind: Record<string, number>;
  mvb_reached_count: number;
  mvb_total_count: number;
  annual_revenue_at_risk_sar: string;
  daily_revenue_at_risk_sar: string;
}

export interface TwinBlastGraph {
  as_of: string;
  root_kind: TwinNodeKind;
  root_id: string;
  reached_node_ids: string[];
  summary: TwinBlastSummary;
  chain_seal: ChainSeal;
}

export interface TwinBlastHistory {
  entity_kind: string;
  entity_id: string;
  snapshots: ChainSeal[];
}

export type ProvenanceInputKind = "import" | "snapshot" | "feed" | "declaration";

export interface ProvenanceInput {
  kind: ProvenanceInputKind;
  label: string;
  source: string;
  hash: string;
  ref: string | null;
  detail: string | null;
  /** Optional drill-down handle for snapshot-kind inputs. The
   * frontend opens a nested ProvenanceTrace by passing these
   * coordinates to useProvenance(). */
  trace_ref: { chain: string; entity_kind: string; entity_id: string } | null;
}

export interface ProvenanceTrace {
  chain: string;
  entity_kind: string;
  entity_id: string;
  title: string;
  value: string;
  formula: string;
  deriver: string;
  inputs: ProvenanceInput[];
  output: ChainSeal;
}

// ── 18. Capability map graph (P85.1) ──────────────────────────────────

export interface CapabilityZone {
  id: string;
  label: string;
  band: string;
}

export interface CapabilityCell {
  id: string;
  code: string;
  name: string;
  description: string | null;
  zone_code: string | null;
  tier: number;
  mvb_flag: boolean;
  open_finding_count: number;
  linked_ibs_ids: string[];
}

export interface CapabilityDependencyEdge {
  dependent_id: string;
  upstream_id: string;
}

export interface CapabilityGraph {
  as_of: string;
  zones: CapabilityZone[];
  capabilities: CapabilityCell[];
  edges: CapabilityDependencyEdge[];
}

// ── IBS admission (P96/P97) ─────────────────────────────────────────

export interface IBSScoreProvenance {
  score: number;
  data_extract: string;
  query_reference: string;
  date_pulled: string;
}

export interface IBSNbsGateEvidence {
  /** P416 -- which of the rubric's gates the `q1`/`q2` slots hold:
   *  SAMA's Q1/Q2, or an OSFI critical operation's G1/G2. Absent on rows
   *  written before P416, which are always Q1/Q2. */
  gate_codes?: { q1: string; q2: string };
  q1: {
    engine_answer: boolean;
    breadth_score: number | null;
    has_revenue_products: boolean;
  };
  /**
   * Q2 is the WORKAROUND test, per the founding methodology: "workaround
   * available; customer can still achieve the objective". Grounded in
   * the continuity posture of the supporting services, read at the
   * weakest link.
   *
   * Every field is optional because this is persisted JSONB and
   * decisions taken before the reconciliation carry the older
   * financial-materiality shape. A missing key reads as "not asked", a
   * null as "asked, not applicable".
   */
  q2: {
    engine_answer: boolean;
    services_total?: number;
    services_with_posture?: number;
    services_without_workaround?: string[];
    services_with_stale_proof?: string[];
    /** null -> no posture recorded, so it cannot be judged. */
    evidence_workaround_exists?: boolean | null;
    evidence_meaningful_impact?: boolean | null;
    consistent: boolean | null;
    basis_unavailable_reason: string | null;
    /** Round 20 G45 -- the challenge's answer to a disagreement. */
    reconciliation?: { text: string; by: string | null; at: string } | null;
    /** Pre-reconciliation decisions only. */
    financial_basis_sar?: string | null;
    threshold_sar?: string;
    impact_tolerance_hours?: number | null;
  };
  /**
   * The financial figures. Evidence for severity and firm safety, NOT a
   * gate test -- the source has no financial-materiality question at
   * qualification. Absent on pre-reconciliation decisions.
   */
  impact_magnitude?: {
    threshold_sar: string;
    impact_tolerance_hours: number | null;
    revenue_annual_sar: string;
    service_impact_per_hour_sar: string;
    financial_basis_sar: string | null;
    basis_driver: string | null;
    material: boolean | null;
    basis_unavailable_reason: string | null;
  };
}

export interface IBSAdmissionDecision {
  id: string;
  ibs_service_id: string;
  /** A pathway code from the regime's rubric (P416), `cif`, or the
   *  pre-0160 `standard`/`sms` on decisions taken before reconciliation. */
  pathway:
    "standard" | "sms" | "ces" | "ids" | "cif" | "critical_operation" | (string & {});
  /** P416 -- whose rubric decided this row, and its edition; edition
   *  null on rows decided before rubrics were data files. */
  regime_code?: string | null;
  rubric_version?: string | null;
  /** P429 -- whose second signature approves: the business owner, or
   *  the CIO for a Critical Internal Function (Route 2). */
  second_signer_role?: "business_owner" | "cio";
  inclusion_decision: "included" | "excluded" | "deferred";
  is_nbs: boolean;
  triggers_fired: string[];
  gate_failed_reason: string | null;
  criticality_tier: "tier_0" | "tier_1" | "tier_2" | "tier_3" | null;
  scores: Record<string, number>;
  rationale_by_criterion: Record<string, string> | null;
  // P218 (gap 5): per-criterion evidence-pack provenance.
  score_provenance: Record<string, IBSScoreProvenance> | null;
  // P219 (gap 2): evidence behind the NBS gate.
  nbs_gate_evidence: IBSNbsGateEvidence | null;
  threshold_rto_hours: number | null;
  threshold_rpo_hours: number | null;
  threshold_max_outage_hours: number | null;
  threshold_impact_tolerance: string | null;
  source: "engine" | "manual";
  workflow_state:
    | "draft"
    | "challenged"
    | "cro_signed"
    | "business_owner_signed"
    | "cio_signed"
    | "approved";
  decided_at: string;
  version: number;
  is_active: boolean;
  challenge_reviewer_name: string | null;
  challenge_at: string | null;
  challenge_note: string | null;
  /** B1 -- the signature history the record stores and never showed. */
  cro_signoff_name?: string | null;
  cro_signoff_at?: string | null;
  cro_signoff_note?: string | null;
  business_owner_signoff_name?: string | null;
  business_owner_signoff_at?: string | null;
  business_owner_signoff_note?: string | null;
  /** Round 20 V1 -- the leg the second-line challenger also signed. */
  challenger_signed?: string | null;
  /** Round 20 G44 -- the signed tolerance these placeholders yield to. */
  thresholds_superseded_by?: { hours: number | null; signed_by: string | null } | null;
  /** Round 20 A46 -- legs dated before the row was written, and when it was. */
  signed_before_created?: string[];
  created_at?: string | null;
}

export interface IBSCandidate {
  /** A4 -- which criterion set the manual score form must ask for.
   *  Derived by the API: `route_pathway` reads the sovereign flag AND
   *  a code/name heuristic, so the client cannot infer it safely. */
  pathway?: string;
  id: string;
  code: string;
  name: string;
  outcome_category: string;
  sovereign_flag: boolean;
  admission_status: "candidate" | "admitted" | "removed";
  // P220 (gap 1): intake provenance tag.
  provenance_stream: "catalogue" | "nbs" | "regulator_named" | null;
  /** Round 18 N11 -- who signs the business owner's leg. */
  owner_name?: string | null;
  /** Round 20 W3 -- false when the owner is named with no sign-in address. */
  owner_bound?: boolean;
  /** Round 20 N15 -- the session is the recorded owner, and whether the
   *  owner holds the business-owner seat (null: no account yet). */
  viewer_is_owner?: boolean;
  owner_holds_seat?: boolean | null;
  /** Round 20 W10 -- why this pathway: sovereign | rail_name | customer. */
  pathway_reason?: string | null;
  latest_decision: IBSAdmissionDecision | null;
}

/** P103: a CMDB service eligible to be promoted into an IBS candidate. */
export interface PromotableService {
  id: string;
  code: string;
  name: string;
  classification: string;
  /** Round 19 L14 -- the bank's own mark of an internal tool. */
  internal_tool?: boolean;
}

/**
 * A1: the candidates, with enough to say why there are none.
 *
 * A flat list rendered an empty register, an estate whose every
 * service is already promoted, and a failed request as one sentence.
 * Those need three different moves from the reader, so the panel needs
 * to tell them apart.
 */
export interface PromotableServices {
  services: PromotableService[];
  total_services: number;
  promoted_count: number;
  out_of_scope_count: number;
}

/** P151: one admitted IBS's review-cadence + drift status. */
export interface IBSReviewRow {
  ibs_service_id: string;
  ibs_code: string;
  ibs_name: string;
  decision_id: string;
  version: number;
  criticality_tier: string | null;
  inclusion_decision: string;
  reviewed_at: string | null;
  next_review_due: string | null;
  review_status: "current" | "due_soon" | "overdue" | "unscheduled";
  drift: "new" | "stable" | "promoted" | "demoted";
  prior_tier: string | null;
}

export interface IBSReviewScan {
  today: string;
  overdue_count: number;
  due_soon_count: number;
  rows: IBSReviewRow[];
}

// ── MVB candidate selection (P110/P111) ───────────────────────────────

export type MvbTier = "core_mvb" | "mvb" | "watchlist" | "out_of_scope";

export interface MvbCandidateRow {
  id: string;
  entity_kind: "ibs_service" | "service" | "vendor";
  entity_id: string;
  provenance: "ibs_register" | "supporting_service" | "regulator_mandated";
  display_name: string | null;
  current_tier: MvbTier | null;
  active_pack_id: string | null;
  composite: number | null;
  recommendation: string | null;
  escalate: boolean;
  /** Round 17 L6 -- an IBS-register candidate's class and admission. */
  service_class?: string | null;
  admission_status?: string | null;
}

export interface MvbCandidateList {
  candidates: MvbCandidateRow[];
  /** Round 18 P7 -- the registers this one is scored from, and how many
   * of their rows are not yet scanned (a removed row is not a candidate). */
  register_services?: number;
  register_functions?: number;
  unscanned_services?: number;
  unscanned_functions?: number;
}

export interface MvbScoreEntry {
  value: number;
  data_extract: string;
  query_ref: string;
  pulled_at: string;
}

export interface MvbPack {
  id: string;
  candidate_id: string;
  version: number;
  is_active: boolean;
  scores: Record<string, MvbScoreEntry>;
  gate1_continuity_critical: boolean;
  gate1_reason: string | null;
  gate1_evidence: Record<string, unknown>;
  gate2_external: boolean;
  gate2_reason: string | null;
  gate2_evidence: Record<string, unknown>;
  composite: number;
  base_tier: MvbTier;
  tier: MvbTier;
  tie_break_applied: boolean;
  escalate: boolean;
  notes: string[];
  thresholds_used: Record<string, unknown>;
  recommendation: string | null;
  source: "engine" | "manual" | "signal";
  workflow_state: "draft" | "challenged" | "partially_signed" | "approved";
  evaluated_at: string;
  challenge_reviewer_name: string | null;
  challenge_reviewer_email: string | null;
  challenge_note: string | null;
  challenge_at: string | null;
  cro_signoff_name: string | null;
  cro_signoff_at: string | null;
  /** D8: the caveat the signer put beside their name. */
  cro_signoff_note?: string | null;
  ae_signoff_name: string | null;
  ae_signoff_at: string | null;
  ae_signoff_note?: string | null;
}

export interface MvbCandidateDetail {
  id: string;
  entity_kind: string;
  entity_id: string;
  provenance: string;
  display_name: string | null;
  current_tier: MvbTier | null;
  version_count: number;
  prior_tier: MvbTier | null;
  drift: "new" | "stable" | "promoted" | "demoted";
  active_pack: MvbPack | null;
}

export interface MvbDeriveResult {
  candidates: number;
  scored: number;
  by_tier: Record<string, number>;
}

export interface MvbManualScoresIn {
  // P326 dropped customer_reach, P327 dropped vendor_concentration -- both
  // derive from live data now. substitutability is the sole manual criterion.
  substitutability?: number;
}

export interface MvbChallengeIn {
  /** A5 -- the reviewer is the authenticated session; only the note is
   *  typed. A name that could be typed could be somebody else's. */
  note?: string | null;
}

// ── Per-user preferences (P115) ───────────────────────────────────────

export interface UserPreferences {
  dismissed_rails: string[];
  last_role_used: string | null;
  digest_mode?: "daily" | "weekly" | null;
  /** Stitching review 5.5 -- glossary keys this person has met. */
  met_terms?: string[];
}

export interface MvbSignoffIn {
  /** A5 -- the seat only; the signer is the session, so the register
   *  and the inbox record the same actor for the same act. */
  role: "cro" | "ae";
}

export interface MvbProposedMember {
  entity_kind: string;
  entity_id: string;
  display_name: string | null;
  tier: MvbTier;
}

export interface MvbClosureGap {
  ibs_id: string;
  ibs_name: string | null;
  service_id: string;
  service_name: string | null;
  service_tier: string | null;
}

export interface MvbConvergence {
  proposed: MvbProposedMember[];
  closure_gaps: MvbClosureGap[];
  journeys_total: number;
  journeys_covered: number;
  journeys_uncovered: { ibs_name: string | null; channel_name: string | null }[];
  reconciliation: {
    aligned_count: number;
    pending_approval: string[];
    demotion_candidates: string[];
  };
  summary: {
    proposed_count: number;
    proposed_ibs_count: number;
    proposed_service_count: number;
    closure_gap_count: number;
  };
  /** P439: when an EA import last changed the architecture graph,
   *  and whether convergence has been reviewed since. */
  architecture_review?: MvbArchitectureReview | null;
}

export interface MvbArchitectureReview {
  changed_at: string;
  filename: string;
  file_sha256: string;
  reviewed_at: string | null;
  stale: boolean;
}

/** P103: the four IBS outcome categories (mirrors backend
 *  `_IBS_OUTCOME_CATEGORIES`). */
export const IBS_OUTCOME_CATEGORIES = [
  "minimize_customer_harm",
  "retain_customer_confidence",
  "regulatory_engagement",
  "protect_safety_and_soundness",
] as const;

// ── Non-SoR data ingest dictionary (P117.1) ────────────────────────

export interface ImportFieldSpec {
  name: string;
  type: string; // "string" | "int" | "decimal" | "bool" | "enum" | "code_ref"
  required: boolean;
  description: string;
  example: string;
  enum_values: string[];
  max_length: number | null;
  sama_mandated: boolean;
  target_column: string | null;
  ref_entity: string | null;
}

export interface ImportEntityDictionary {
  kind: string;
  label: string;
  /** Round 10 D2: a link's two ends as catalogue keys. */
  label_hops?: string[];
  natural_key: string;
  template_url: string;
  field_count: number;
  required_count: number;
  sama_mandated_count: number;
  /** P150: a graph-edge junction (keyed by a code_ref pair) rather than
   * a primary entity. Not Engine-assistable. */
  is_link: boolean;
  /** P259: an owned child record (e.g. a journey step) keyed by its parent
   * + a positional column. Like a link, structured-CSV only (not
   * Engine-assistable). */
  is_child: boolean;
  fields: ImportFieldSpec[];
}

export interface ImportDictionary {
  entities: ImportEntityDictionary[];
  /** Round 20 W4 -- what does not import from a file, and why. */
  not_importable?: NotImportable[];
}

export interface NotImportable {
  kind: string;
  label: string;
  reason: string;
  route: string;
  route_link?: string | null;
}

// ── CSV import staging + completeness (P117.2 / P117.3) ─────────────

export interface ImportRow {
  row_number: number;
  action: string | null; // "insert" | "update" | null
  verdict: string; // "ok" | "error"
  /** Round 13 I2: `code` is the catalogue key and `message` the
   *  English the server composed. The message is the FALLBACK, so a
   *  code the catalogue has not learned yet still reads -- adding a
   *  code can never blank a row error that used to say something. */
  errors: {
    field: string;
    message: string;
    code?: string;
    params?: Record<string, unknown>;
  }[];
  /** Round 13 D8: things worth saying that are NOT refusals -- today, a
   * display name this row shares with something else. The verdict stays
   * "ok"; a bank may legitimately run two services under one name, so
   * the operator decides rather than the importer. */
  warnings?: { field: string; message: string }[];
  target_code: string | null;
  committed_target_id: string | null;
  /* Round 12 A6 -- a graph edge has no register of its own, but it does
     have an anchor: the entity whose drawer lists it. */
  committed_parent_kind?: string | null;
  committed_parent_id?: string | null;
  /** The row's cells as read. Round 13 D9 removed this from the
   * FOUNDATION importer's listing -- it made a 5,000-row dry run four
   * and a half times the size of the file and no client ever read it.
   * The framework-taxonomy importer, which shares this shape and whose
   * batches are one row per control, still sends it. */
  raw?: Record<string, string>;
}

export interface ImportBatch {
  id: string;
  entity_kind: string;
  status: string; // "staged" | "committed" | "failed"
  filename: string | null;
  row_count: number;
  ok_count: number;
  error_count: number;
  insert_count: number;
  update_count: number;
  created_at: string;
  committed_at: string | null;
  rows: ImportRow[];
  /** P137: the Engine column-mapping when this batch came from the
   * assisted path; null for a structured upload. */
  engine_mapping?: EngineMapping | null;
  /** Round 13 D1: source columns this entity does not recognise, each
   * with the closest recognised field where that is unambiguous. Empty
   * when the header matched entirely -- a reader is never told about a
   * loss that did not happen. */
  ignored_columns?: IgnoredColumn[];
  /** Round 13 D2: how many rows point at a parent this same file
   * creates. Zero on almost every batch; when it is not, it is the
   * answer to "why did this work when the same file used to need a
   * second upload?". */
  self_referencing_rows?: number;
  /** Round 13 D9: clean rows the listing did not carry. Never an error
   * row -- those are what a person acts on, so all of them are sent. */
  rows_not_listed?: number;
  /** Round 13 D8: when this exact file was last COMMITTED for this
   * entity, if it has been. Null on the ordinary case. Without it a
   * second upload of the same spreadsheet reads as "0 insert, N
   * update" -- indistinguishable from somebody having edited a row. */
  previously_committed_at?: string | null;
  /** Round 13 D8: how many rows name something the estate, or this same
   * file, already names. Zero on almost every batch. */
  duplicate_name_rows?: number;
}

/** Round 13 D8 -- one line of the importer's history. Deliberately
 * WITHOUT the rows: a history carrying five thousand rows per entry
 * would be the payload defect D9 is about, and the detail is one click
 * away. */
export interface ImportBatchSummary {
  id: string;
  entity_kind: string;
  status: string;
  filename: string | null;
  row_count: number;
  ok_count: number;
  error_count: number;
  insert_count: number;
  update_count: number;
  created_at: string;
  committed_at: string | null;
}

/** Round 13 D8 -- what an undo managed to reverse, and what it would
 * not. Partial by design: a record the estate has since built on, or
 * one a person has since corrected, is named rather than forced. */
export interface ImportUndoResult {
  reverted_inserts: number;
  reverted_updates: number;
  refused: { row: string; code: string; reason: string }[];
  status: string;
}

/** One source column the importer did not recognise. `suggestion` is
 * offered only where the comparison is unambiguous: guessing between two
 * fields is how a value lands in the wrong column. */
export interface IgnoredColumn {
  column: string;
  suggestion?: string;
  /** A column the entity used to take; the field that replaced it. */
  retired_for?: string;
}

/** P260: a custom-framework taxonomy import batch (reuses the ImportRow
 * per-row verdict shape; no entity_kind / engine_mapping). */
export interface FrameworkImportBatch {
  id: string;
  status: string; // "staged" | "committed"
  filename: string | null;
  row_count: number;
  ok_count: number;
  error_count: number;
  insert_count: number;
  update_count: number;
  created_at: string;
  committed_at: string | null;
  rows: ImportRow[];
}

export interface EngineMapping {
  mappings: {
    target_field: string;
    source_column: string;
    confidence: number;
  }[];
  unmapped_source_columns: string[];
  unmapped_target_fields: string[];
  model: string | null;
  /** P238: Engine-resolved enum synonyms, {field: {messy: canonical}}. */
  enum_overrides?: Record<string, Record<string, string>> | null;
}

export interface ImportFieldCoverage {
  name: string;
  target_column: string;
  populated: number;
  total: number;
  pct: number;
}

export interface ImportEntityCompleteness {
  kind: string;
  label: string;
  total_rows: number;
  overall_pct: number;
  fields: ImportFieldCoverage[];
}

export interface ImportCompleteness {
  entities: ImportEntityCompleteness[];
}

// ── Service accounts / API keys (P119.4) ────────────────────────────

export const SERVICE_SCOPES = [
  "findings:read",
  "assessments:read",
  "board:read",
  "cascade:read",
  // CX round 8: the six-layer dependency map, paged by code. Absent
  // from this list until P427, so the picker could not mint the key
  // the backend already accepted.
  "dependencies:read",
  // P427 A: the whole IBS register as one document, for a GRC
  // platform's data feed (Archer's HTTP transporter takes one JSON
  // document per run).
  "ibs_register:read",
  // P185: the first WRITE scope -- admits an external monitor to push
  // live service-health samples (POST /v1/ext/telemetry).
  "telemetry:write",
] as const;

export interface ServiceAccount {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  enabled: boolean;
  last_used_at: string | null;
  /** Round 9 A1: authenticated external requests this key has made. */
  request_count: number;
  last_rotated_at: string | null;
  created_at: string;
}

export interface ServiceAccountCreated extends ServiceAccount {
  api_key: string; // shown ONCE
}

export interface ServiceAccountCreateIn {
  name: string;
  scopes: string[];
}

export interface ServiceAccountPatchIn {
  enabled?: boolean;
  scopes?: string[];
}

// ── Attestation programme (P131, #483) ────────────────────────────────

export type AttestationCycleStatus = "draft" | "open" | "closed";
export type AttestationStatus = "draft" | "submitted" | "approved" | "rejected";

export interface AttestationCycle {
  id: string;
  name: string;
  description: string | null;
  period_start: string;
  period_end: string;
  due_date: string;
  status: AttestationCycleStatus;
  display_order: number | null;
  created_at: string;
  updated_at: string;
  counts: Record<AttestationStatus, number>;
  attestation_count: number;
}

export interface AttestationCycleIn {
  name: string;
  description?: string | null;
  period_start: string;
  period_end: string;
  due_date: string;
  display_order?: number | null;
}

export interface Attestation {
  id: string;
  cycle_id: string;
  ibs_service_id: string;
  ibs_code: string | null;
  ibs_name: string | null;
  status: AttestationStatus;
  attestation_text: string | null;
  comments: string | null;
  snapshot: Record<string, unknown>;
  /**
   * C5: the IBS as it stands NOW. `snapshot` is frozen at submit and is
   * empty on a draft, so this is what a signatory reads before
   * committing their name to it.
   */
  ibs_summary: Record<string, unknown> | null;
  submitter_name: string | null;
  submitter_email: string | null;
  approver_name: string | null;
  approver_email: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AttestationIn {
  cycle_id: string;
  ibs_service_id: string;
  attestation_text?: string | null;
}

export interface AttestationIbsOption {
  id: string;
  code: string;
  name: string;
  /** Round 16 L4 -- functions are offered under their own heading. */
  service_class?: "customer" | "state" | "internal";
}

export type AttestationAction = "submit" | "approve" | "reject" | "reopen";

// ── Resilience drills / BCP tests (P132, #482) ────────────────────────

export type DrillType = "bcdr" | "tabletop" | "failover" | "chaos" | "scenario";
export type DrillStatus = "planned" | "in_progress" | "passed" | "failed" | "cancelled";
export type DrillAction = "start" | "pass" | "fail" | "cancel";

export interface ResilienceDrill {
  id: string;
  ibs_service_id: string;
  ibs_code: string | null;
  ibs_name: string | null;
  scenario_id: string | null;
  name: string;
  description: string | null;
  drill_type: DrillType;
  status: DrillStatus;
  scheduled_for: string;
  started_at: string | null;
  completed_at: string | null;
  target_rto_minutes: number | null;
  target_rpo_minutes: number | null;
  actual_rto_minutes: number | null;
  actual_rpo_minutes: number | null;
  owner_name: string | null;
  owner_email: string | null;
  outcome_notes: string | null;
  raised_finding_id: string | null;
  created_at: string;
  updated_at: string;
  /** Round 20 V3 -- the service's tolerance, and a pass its figures contradict. */
  tolerance_minutes?: number | null;
  pass_contradiction?: "not_measured" | "missed_target" | "over_tolerance" | null;
  pass_contradicted_by_minutes?: number | null;
  /** Round 20 Y7 -- the target set above the signed tolerance, and the
   *  actual read against both. */
  target_above_tolerance_minutes?: number | null;
  against_target?: "met" | "missed" | null;
  against_tolerance?: "within" | "over" | null;
}

export interface DrillIn {
  ibs_service_id: string;
  scenario_id?: string | null;
  name: string;
  description?: string | null;
  drill_type: DrillType;
  scheduled_for: string;
  target_rto_minutes?: number | null;
  target_rpo_minutes?: number | null;
  owner_name?: string | null;
  owner_email?: string | null;
}

export interface DrillIbsOption {
  id: string;
  code: string;
  name: string;
  /** Round 16 L4 -- functions are offered under their own heading. */
  service_class?: "customer" | "state" | "internal";
}

// ── Notifications (P133, #480) ────────────────────────────────────────

export interface AppNotification {
  /**
   * G1: the catalogue key + parameters the server composed this from.
   * Absent on older rows and on callers not yet keyed, where `title`
   * and `body` remain the fallback.
   */
  message_key?: string | null;
  message_params?: Record<string, unknown> | null;
  id: string;
  kind: string;
  status: "unread" | "read" | "dismissed";
  title: string;
  body: string | null;
  link: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
  // P346 acknowledgement loop.
  ack_required?: boolean;
  acknowledged_at?: string | null;
}

export interface EmailFailureRow {
  id: string;
  kind: string;
  title: string;
  attempts: number;
  error: string | null;
  created_at: string;
}

export interface EmailHealth {
  pending: number;
  digest_held: number;
  failed: number;
  recent_failures: EmailFailureRow[];
}

// ── Proof signing posture (P179) ──────────────────────────────────────

export interface ProofSigningStatus {
  algorithm: string;
  key_id: string;
  is_ed25519: boolean;
  is_sentinel: boolean;
  require_ed25519: boolean;
  published_key_count: number;
}

// ── Engine control<->CAP suggestion (P139) ────────────────────────────

export interface ControlSuggestion {
  framework_control_id: string;
  control_natural_id: string;
  framework_code: string;
  control_text: string;
  confidence: number;
  rationale: string;
}

// ── Provable assurance -- "answer the regulator" (P154) ───────────────

export interface AssuranceQuestion {
  id: string;
  text: string;
  /** ROUND 15 H11 -- the catalogue key, with `text` as the fallback. */
  text_key?: string;
}

export interface AssuranceCitation {
  kind: string;
  ref_id: string;
  label: string;
  surface_url: string | null;
  /** Slug of the hash chain backing this claim (the "with proof" hook). */
  chain: string | null;
}

export interface AssuranceClaim {
  label: string;
  value: string;
  citations: AssuranceCitation[];
}

export interface AssuranceAnswer {
  question_id: string;
  question_text: string;
  as_of: string;
  generated_at: string;
  claims: AssuranceClaim[];
  note: string | null;
}

// ── Regulatory-change autopilot (P158) ────────────────────────────────

export interface AffectedObligation {
  obligation_id: string;
  natural_id: string;
  title: string;
  source_framework: string | null;
  impact_type: "new_requirement" | "tightened" | "clarified";
  rationale: string;
  control_count: number;
  gap_count: number;
}

export interface RegulatoryChangeAssessment {
  affected: AffectedObligation[];
  candidates_considered: number;
  obligations_affected: number;
  obligations_with_gaps: number;
  model: string | null;
  grounding: EngineGrounding | null;
  /** Round 19 A42 -- the record written when the assessment was made. */
  assessment_id?: string | null;
}

/** P159: the autopilot's "act" step -- draft a remediation CAP from a
 *  change's affected obligations, pre-linked to the gap controls. */
export interface DraftRemediationCapIn {
  title: string;
  change_text: string;
  obligation_ids: string[];
  /** Round 19 A42 -- the assessment the plan is drafted from. */
  assessment_id?: string | null;
}

export interface DraftRemediationCapOut {
  cap_id: string;
  letter_id: string;
  assessment_id: string;
  linked_controls: number;
  /** Round 18 A33 -- implementing controls graded compliant, left off. */
  compliant_not_linked?: number;
}

/** P168: sector benchmark -- the bank's posture metric vs a sector
 *  baseline (idea #5 slice 1). */
export interface EngineOutputRow {
  id: string;
  surface: string;
  entity_id: string;
  model: string | null;
  /** ROUND 15 A15 -- sealed since hash version 2. */
  configured_model?: string | null;
  total_returned: number;
  grounded: number;
  dropped_ungrounded: number;
  prompt_sha256: string;
  response_sha256: string;
  row_hash: string;
  captured_at: string;
  captured_by: string;
}

// ── Twin calibration (P180.3/4) ───────────────────────────────────────

export interface TwinCalibrationRow {
  entity_kind: string;
  entity_id: string;
  entity_name: string;
  modelled_hours: number | null;
  observed_mean_hours: number;
  sample_count: number;
  error_pct: number | null;
  suggestion: "ok" | "raise" | "lower" | "no_model" | "insufficient_data";
  source: "incidents" | "drills";
  /** P228: for IBS rows -- the recovery override the apply would write. */
  suggested_override?: number | null;
}

/** P228: result of applying an IBS's observed recovery as its override. */
export interface ApplyIbsCalibration {
  ibs_service_id: string;
  ibs_name: string;
  old_override: number | null;
  new_override: number;
  observed_mean_hours: number;
  binding_rto_hours: number;
  sample_count: number;
  changed: boolean;
}

export interface TwinCalibrationReport {
  as_of: string;
  services_sampled: number;
  ibses_sampled: number;
  // P196: services with telemetry-derived outage episodes.
  telemetry_sampled: number;
  flagged_count: number;
  mean_abs_error_pct: number | null;
  service_rows: TwinCalibrationRow[];
  ibs_rows: TwinCalibrationRow[];
  telemetry_rows: TwinCalibrationRow[];
}

export interface BenchmarkMetric {
  id: string;
  label: string;
  unit: string;
  direction: "higher_better" | "lower_better";
  /** Round 17 B14 -- null when the metric has no denominator. */
  value: number | null;
  baseline: number;
  delta: number | null;
  verdict: "ahead" | "on_par" | "behind" | "not_comparable";
  /** Why it cannot be compared, or whose rows it was measured over. */
  population?: string | null;
}

/** P363: k-anonymous peer percentiles for one metric (suppressed
 *  below the aggregator's k floor). */
export interface PeerPercentiles {
  n: number;
  p25?: number | null;
  p50?: number | null;
  p75?: number | null;
  suppressed?: boolean;
}

export interface BenchmarkReport {
  generated_at: string;
  baseline_source: string;
  /** P363 labelling contract: "aggregator" (live peers) or "synthetic". */
  peer_source: string;
  peer_contributors: number | null;
  peers: Record<string, PeerPercentiles>;
  metrics: BenchmarkMetric[];
  /** A4: the aggregator a submission would be sent to, or null when
   *  no peer network is configured (submission impossible). */
  destination?: string | null;
}

/** P165: a persisted regulatory-change lineage row -- a circular the
 *  autopilot acted on + the CAP it raised. */
export interface ChangeAssessment {
  id: string;
  change_text: string;
  obligation_count: number;
  /** Round 19 A42 -- null for an assessment no plan was drafted from. */
  letter_id: string | null;
  cap_id: string | null;
  cap_title: string | null;
  cap_state: string | null;
  created_by_username: string | null;
  created_at: string;
  /** Round 19 N13 -- the obligations it found, so a preparer handed
   *  the assessment can draft the plan from its row. */
  obligation_ids?: string[];
}

/** P153: grounded-AI verdict -- `dropped` = citations the Engine
 *  invented that were removed because they aren't real sources. */
export interface EngineGrounding {
  returned: number;
  grounded: number;
  dropped: number;
  /* Round 12 D1 -- how many sources the model could choose from, and
     whether that number was one. A citation drawn from a shortlist of
     one is not a judgement; it is the only move available. */
  candidate_count?: number | null;
  nominal_attribution?: boolean;
  /** P173: true when the Engine invented more than it grounded
   *  (coverage below the confidence floor) -> low-confidence warning. */
  below_floor?: boolean;
  /** P232: true when hard-refusal mode dropped the result (below the
   *  floor) -> the surface returned nothing. */
  refused?: boolean;
}

export interface SuggestControls {
  suggestions: ControlSuggestion[];
  candidates_considered: number;
  model: string | null;
  grounding: EngineGrounding | null;
}

/** P164: Engine-drafted CAP remediation actions, grounded in the CAP's
 *  linked controls + persisted into the draft CAP. */
export interface DraftActions {
  entries: CAPEntryOut[];
  observation_id: string;
  model: string | null;
  grounding: EngineGrounding | null;
}

// ── Shared async batch runs (P143 obligation passes -> P144 generic) ──

export type EngineBatchStatus =
  "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** One async background batch pass (any module / kind) -- the shared
 *  engine_batch_runs substrate. `total`/`processed` drive a progress bar
 *  for slow per-item passes (obligation summarise); NULL/0 for fast
 *  derivers (the twin chain derivers) which just show a spinner + result. */
export interface EngineBatchRun {
  id: string;
  kind: string;
  module: string;
  status: EngineBatchStatus;
  total: number | null;
  processed: number;
  result: Record<string, number> | null;
  /* Round 12 C1 -- the model this run RAN ON: the one a provider named
     in its response where it named one, else the configured value with
     `model_observed` false. The column headed "Model" printed the
     configuration and read as a reading, so four runs showed as one
     mistral and three stub-engine where the truth is two and two. */
  model: string | null;
  configured_model?: string | null;
  model_observed?: boolean;
  error_detail: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

// ── Engine cross-framework obligation dedup (P140) ────────────────────

export interface ObligationEquivalent {
  id: string;
  natural_id: string;
  title: string;
  source_framework: string | null;
  citation_ref: string | null;
  confidence: number;
  rationale: string;
  /** P231: already a saved equivalence of the source obligation. */
  already_confirmed?: boolean;
}

export interface ObligationEquivalents {
  equivalents: ObligationEquivalent[];
  candidates_considered: number;
  model: string | null;
  grounding: EngineGrounding | null;
}

/** P231: a persisted (confirmed) cross-framework equivalence -- the
 *  other obligation in the pair. */
export interface ConfirmedEquivalence {
  link_id: string;
  obligation_id: string;
  natural_id: string;
  title: string;
  source_framework: string | null;
  citation_ref: string | null;
  confidence: number | null;
  source: "engine" | "manual";
}

/**
 * P414: a regulator's regime, as reference data.
 *
 * Read-only by design. Adding one is a migration with its sources
 * named, not a form -- the fixed metamodel is what lets the product
 * make regulatory assertions at all.
 */
export interface Regime {
  code: string;
  regulator_name: string;
  regulator_short: string;
  country_code: string;
  /** A SET, not a country: DORA's home is 27 of them. */
  home_country_codes: string[];
  /** What the `_sar` columns are actually denominated in. */
  currency_code: string;
  /** BCP 47, authoritative text first. */
  languages: string[];
  service_term_key: string;
  institution_term_key: string;
  tolerance_term_key: string;
  /** null = this regime's deadline is not recorded yet (P422 fills it). */
  is_active: boolean;
}

export interface RegimeList {
  count: number;
  regimes: Regime[];
}

/** P251: deployment bank identity for branded outputs. */
export interface BankProfile {
  /**
   * F3 / P414: which regime this bank is under, as a `regimes.code`.
   * null = not recorded, and surfaces say so rather than assuming.
   * Renamed from `jurisdiction`: the old name described a string, this
   * one names the object it points at.
   */
  regime_code?: string | null;
  display_name: string;
  accent_hex: string;
  /**
   * Bank-scale figures. null when unsupplied, in which case
   * bank-relative scoring falls back to absolute bands rather than
   * inventing a denominator.
   */
  total_customers: number | null;
  annual_revenue_sar: string | null;
  has_logo: boolean;
  logo_mime: string | null;
  logo_data_uri: string | null;
  updated_by_username: string | null;
  updated_at: string | null;
}

export interface BankProfilePatch {
  display_name?: string;
  accent_hex?: string;
  /** Omit to leave alone; send null to clear. */
  regime_code?: string | null;
  /** Omit to leave alone; send null to clear. */
  total_customers?: number | null;
  annual_revenue_sar?: string | null;
  logo_data_uri?: string | null;
  clear_logo?: boolean;
}

/** P252: a declared data source in the onboarding register. */
export type DataSourceClass = "reference" | "mock" | "demo" | "bank";
export type DataSourceMethod = "connector" | "csv" | "api" | "manual";
export type DataSourceStatus = "defined" | "wired" | "verified";

export interface DataSource {
  id: string;
  name: string;
  data_class: DataSourceClass;
  ingest_method: DataSourceMethod;
  status: DataSourceStatus;
  feeds: string | null;
  location: string | null;
  source_format: string | null;
  owner: string | null;
  is_canonical: boolean;
  notes: string | null;
  updated_at: string | null;
}

export interface DataSourceInput {
  name: string;
  data_class: DataSourceClass;
  ingest_method: DataSourceMethod;
  status: DataSourceStatus;
  feeds?: string | null;
  location?: string | null;
  source_format?: string | null;
  owner?: string | null;
  is_canonical: boolean;
  notes?: string | null;
}

/** P382.3 -- derived readiness for one connected system. */
export type ConnectorReadinessState =
  "not_configured" | "configured" | "verified" | "failing";

export interface ConnectorReadiness {
  connector_id: string;
  kind: string;
  label: string;
  enabled: boolean;
  readiness: ConnectorReadinessState;
  error_streak: number;
  missing_credentials: string[];
}

export interface ConnectionReadiness {
  credential_encryption_configured: boolean;
  enabled_count: number;
  verified_count: number;
  connectors: ConnectorReadiness[];
}

/** P383.1 -- operator-facing setup guidance for one connector kind. */
export interface ConnectorConfigField {
  name: string;
  label: string;
  required: boolean;
  placeholder: string;
  help: string;
}

export interface SetupGuide {
  kind: string;
  system: string;
  summary: string;
  confidence: "verified" | "unverified" | "contract";
  steps: string[];
  gotchas: string[];
  doc_url: string | null;
  /** P438.0: why this connector cannot reach a real system yet. */
  protocol_defect?: string | null;
  config_fields: ConnectorConfigField[];
}

/** B2 -- one assessment waiting on a position, named as a reader needs
 *  it: which cycle, which control. */
export interface AssessmentQueueRow {
  id: string;
  cycle_id: string;
  cycle_name: string;
  framework_code: string;
  framework_name: string;
  /** Round 17 G20 -- the row opens this control. */
  framework_control_id: string;
  control_natural_id: string;
  control_text: string;
  status: string | null;
  workflow_state: string;
  updated_at: string;
}

/** B1 -- a document that could be mapped to a control, and to which deliverable. */
export interface ControlDocumentCandidate extends DocumentReadingCounts {
  document_id: string;
  filename: string | null;
  title: string | null;
  /** B3 -- null where the control has no catalogued deliverable and the
   *  locker is offered directly. Confirming one RECORDS the deliverable
   *  the catalogue was missing. */
  deliverable_id: string | null;
  deliverable_name: string | null;
}

/** B8 -- what the Engine read from a letter PDF, before anything is recorded. */
export interface ExtractedObservation {
  sequence: number;
  title: string;
  body: string;
  severity: string;
  target_capability: string | null;
}

export interface ExtractedLetter {
  proposed_title: string;
  /** Round 16 A26 -- the date the letter states about itself (ISO date). */
  letter_date?: string | null;
  raw_text: string;
  observations: ExtractedObservation[];
}

/** P419 -- one execution of one scenario, with its frozen verdict. */
export interface ScenarioTestRun {
  id: string;
  scenario_id: string;
  scenario_code?: string | null;
  /** Round 16 D22 -- the scenario's name; the row printed its code. */
  scenario_name?: string | null;
  run_at: string;
  method: string;
  intensity: string;
  status: string;
  verdict_summary: string | null;
  facilitator_email: string | null;
  participants: { kind: string; name: string }[] | null;
  prior_run_id: string | null;
  design_changes_from_prior: string | null;
  regime_code: string | null;
  as_of: string | null;
  /** Round 20 V4 -- the scenario was revised after this run. */
  scenario_revised_after?: string | null;
}

/** P419 -- one critical operation against one required scenario class.
 *
 *  Four states, not two. `planned` is its own because a diary entry is
 *  not a test, and `stale` is its own because a test nobody repeated is
 *  not evidence of a current capability. */
export interface CoverageCell {
  ibs_service_id: string;
  ibs_code: string;
  ibs_name: string;
  scenario_class: string;
  state: "tested" | "stale" | "planned" | "untested";
  tested_at?: string | null;
  verdict?: string | null;
  method?: string | null;
  scenario_code?: string | null;
  /** P421 -- stale because a significant change reached it after the run. */
  stale_since_change?: string | null;
  /** Round 20 V4 -- stale because its scenario was revised after the run. */
  stale_since_revision?: string | null;
}

export interface CoverageReport {
  regime_code: string;
  generated_at: string;
  recency_months: number;
  coverage_pct: number | null;
  operations_total: number;
  operations_complete: number;
  deadline?: string | null;
  deadline_days_remaining?: number | null;
  /** P429.5 -- door two: whether Critical Internal Functions count, and
   *  how many admitted ones the regime's door left out. */
  internal_functions_counted?: boolean;
  internal_functions_excluded?: number;
  cells: CoverageCell[];
}

/** P418 -- one member of a composite scenario, with its offset. */
export interface ScenarioComposition {
  id: string;
  member_scenario_id: string;
  member_code: string;
  member_name: string;
  member_class: string;
  offset_hours: number;
  sequence: number;
}

/** P420 -- a continuity plan that was actually exercised. */
export interface PlanTest {
  id: string;
  plan_id: string;
  tested_at: string;
  method: string;
  outcome: string;
  next_test_due: string | null;
  evidence_ref: string | null;
  notes: string | null;
  scenario_test_run_id: string | null;
  resilience_drill_id: string | null;
}

/** P420 -- what an exercise or an incident taught.
 *
 *  `accepted` is NOT `done`: agreeing with a lesson changes nothing,
 *  and `incorporated_at` is the field that says something did. */
export interface LessonLearned {
  id: string;
  source_kind: string;
  source_id: string;
  lesson: string;
  recommended_action: string | null;
  owner_email: string | null;
  raised_at: string;
  status: "open" | "accepted" | "declined" | "done";
  plan_id: string | null;
  cap_entry_id: string | null;
  incorporated_at: string | null;
  declined_reason: string | null;
}

// ── P429.1: Critical Internal Functions (Stream C) ──────────────────

export type CifFunctionKind =
  | "funding_settlement"
  | "liquidity"
  | "collateral"
  | "positions"
  | "ledger"
  | "screening"
  | "prudential_reporting"
  | "other";

export interface CifContagion {
  band: number;
  dependents: string[];
  tightest_dependent_tolerance_hours: number | null;
  /** How F4 was found; a lower bound until the graph carries funding edges. */
  basis: string;
}

export interface CifRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  function_kind: CifFunctionKind;
  owner_name: string | null;
  owner_email: string | null;
  admission_status: string;
  supporting_services: { id: string; code: string; name: string }[];
  contagion: CifContagion;
  latest_decision: IBSAdmissionDecision | null;
  k2_flag: boolean;
}

export interface CifRegister {
  rows: CifRow[];
  admitted_count: number;
  register_size_max: number;
}

export interface CifAnswer {
  value: number;
  rationale: string;
}

export interface CifAssessmentIn {
  qualification: {
    k1_firm_continuity: CifAnswer;
    k2_precedence: CifAnswer;
    k3_non_substitutable: CifAnswer;
  };
  scores: {
    time_to_impairment: CifAnswer;
    firm_severity: CifAnswer;
    regulatory_consequence: CifAnswer;
  };
  rationale?: string;
}

// ── P422 incident reporting per regime ─────────────────────────────

export interface IncidentReportProfile {
  regime_code: string;
  initial_basis: "hours" | "immediately" | "set_by_rts";
  initial_deadline_hours: number | null;
  update_cadence_hours: number | null;
  final_basis: "days" | "on_closure" | "on_resumption" | "set_by_rts";
  final_deadline_days: number | null;
  recipient_label: string;
  form_key: string;
  qualifying_criteria: string[];
  citation: string;
}

export interface IncidentReport {
  id: string;
  finding_id: string;
  regime_code: string;
  stage: "initial" | "update" | "final";
  detected_at: string;
  detected_override_reason: string | null;
  classified_at: string | null;
  /** Null where the regulator states no hours for this stage. */
  due_at: string | null;
  due_basis: string;
  submitted_at: string | null;
  submitted_by_email: string | null;
  reference: string | null;
  status: "draft" | "submitted" | "late";
  submitted_late: boolean;
  /** Round 20 U3: whose reference it is. */
  reference_kind?: "bank_record" | "regulator_issued";
  /** Round 20 U3: what the initial notice says. */
  content?: Partial<Record<NoticeField, string>> | null;
  /** Round 20 U3: required fields still empty. */
  missing_fields?: NoticeField[];
}

/** Round 20 U3 -- the initial notice's fields, in the order they are read. */
export const NOTICE_FIELDS = [
  "what_happened",
  "services_affected",
  "customers_affected",
  "classification",
  "contact",
] as const;
export type NoticeField = (typeof NOTICE_FIELDS)[number];

export interface IncidentReporting {
  regime_code: string;
  regime_source: "recorded" | "default";
  regulator_short: string;
  profile: IncidentReportProfile | null;
  reports: IncidentReport[];
}

/** P429.5 -- the bank's answer to one floor item. */
export interface FloorItemDisposition {
  disposition: "admitted" | "opted_out";
  ibs_service_id: string | null;
  ibs_service_name: string | null;
  ibs_admission_status: string | null;
  justification: string | null;
  recorded_by_email: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  in_force: boolean;
  answered: boolean;
}

/** P429.5 -- an item a regulator pre-admits. */
export interface FloorItem {
  id: string;
  regime_code: string;
  item_key: string;
  label: string;
  service_class: "customer" | "state" | "internal";
  function_kind: string | null;
  source_citation: string;
  disposition: FloorItemDisposition | null;
}

export interface FloorList {
  regime_code: string;
  regime_source: string;
  /** Door two. null is "not confirmed", never read as yes. */
  admits_internal_functions: boolean | null;
  items: FloorItem[];
}

/** Stitching review 5.4 -- one figure from the posture: a count, what it
 * is out of, its population in words, and whether it honoured as_of. */
export interface PostureFigure {
  count: number;
  of: number | null;
  population: string;
  honours_as_of: boolean;
}

/** `GET /v1/posture` -- the bank's headline figures, computed once. */
export interface Posture {
  as_of: string;
  replayed: boolean;
  register_services: PostureFigure;
  register_functions: PostureFigure;
  under_assessment_services: PostureFigure;
  under_assessment_functions: PostureFigure;
  impacted_services: PostureFigure;
  impacted_functions: PostureFigure;
  mvb_impacted: PostureFigure;
  governed: PostureFigure;
  declared: PostureFigure;
  covered: {
    regime_code: string;
    regime_source: string;
    coverage_pct: number | null;
    cells_tested: number;
    cells_total: number;
    honours_as_of: boolean;
  };
  score: {
    score: number | null;
    tier: string;
    confidence: number;
    methodology_version: string;
    last_sealed_score: string | null;
    last_sealed_at: string | null;
    honours_as_of: boolean;
    /** Round 17 B10 -- why there is no score. */
    not_scored_reason?: string | null;
  };
}

/* P421 -- the change register. */
export type ChangeKind =
  | "new_product"
  | "acquisition"
  | "new_market"
  | "technology_system"
  | "process_change"
  | "vendor_change"
  | "facility_change"
  | "regulatory_rebase"
  | "other";

export type AffectedKind =
  | "ibs_service"
  | "service"
  | "vendor"
  | "configuration_item"
  | "facility"
  | "recovery_team"
  | "regime";

export interface ChangeAffected {
  entity_kind: AffectedKind;
  entity_id: string;
  /** The entity's name today; null when it no longer exists. */
  name: string | null;
}

export interface ChangeEventRow {
  id: string;
  kind: ChangeKind;
  title: string;
  description: string | null;
  raised_at: string;
  raised_by_name: string | null;
  effective_from: string | null;
  significance: "significant" | "routine";
  status: "open" | "assessed" | "closed";
  assessment: string | null;
  assessed_by_name: string | null;
  assessed_at: string | null;
  closed_at: string | null;
  /** Round 19 G39 -- who closed it; null while open or unrecorded. */
  closed_by_name?: string | null;
  affected: ChangeAffected[];
  /** Frozen at assessment of a significant change; null before. */
  reached_ibs_count: number | null;
  /** Round 19 G39 -- the services it reached, by name, for links. */
  reached?: { ibs_service_id: string; name: string }[];
  regulatory_change_assessment_id: string | null;
  /** Round 19 G39 -- the regulatory door opened an earlier change. */
  already_raised?: boolean;
  /** Round 19 N12 -- on the raise that fired a watched rule only. */
  told?: ChangeTold | null;
}

/** Round 19 N12 -- whom a raise told now, and the roles it holds the
 *  news for until each holder first signs in. */
export interface ChangeTold {
  rules_fired: number;
  reached: number;
  held_for: string[];
}

export interface ChangeReached {
  ibs_service_id: string;
  code: string;
  name: string;
  current_due: string | null;
  /** Null when the current review is already due sooner. */
  new_due: string | null;
  finding_exists: boolean;
}

export interface ChangePreview {
  significant: boolean;
  review_days: number;
  reviews_pulled_forward: number;
  findings_raised: number;
  reached: ChangeReached[];
  vendors_named: number;
}

export interface Affectable {
  entity_kind: AffectedKind;
  entity_id: string;
  name: string;
  /** Round 19 G39 -- a service marked internal, folded away by default. */
  internal_tool?: boolean;
}

export interface ChangeIn {
  kind: ChangeKind;
  title: string;
  description?: string | null;
  effective_from?: string | null;
  significance: "significant" | "routine";
  affected: { entity_kind: AffectedKind; entity_id: string }[];
}
